/**
 * Gateway protocol types for the OpenClaw WebSocket connection.
 *
 * The LexIDE frontend connects to the per-user OpenClaw gateway via WebSocket.
 * All case content operations and AI chat flow through typed RPC calls and
 * EventFrame streaming.
 */

import type { DocumentType, DocumentStatus } from "@/lib/document-type-registry";
import type { MatterType } from "@/lib/case-labels";
// Type-only (erased at compile time) — keeps this pure types module React-free.
import type { DisplayNames } from "@/lib/entity-display";
import {
  normalizeMessageContent,
  type ContentBlock,
} from "@/types/content-blocks";

// Re-export so callers can keep importing `TodoItem` from `@/types/gateway`
// (the type physically lives in content-blocks.ts now, but the import
// surface stays stable).
export type { TodoItem } from "@/types/content-blocks";

/* ---------- EventFrame (streaming protocol) ---------- */

export type EventFrameEvent = "chat" | "agent" | "task" | "session.tool";

export type ChatEventState = "delta" | "final" | "error" | "aborted";
export type AgentEventPhase = "start" | "end";
export type TaskEventState = "progress" | "final" | "error";

/** Raw chat event payload as emitted by the gateway (nested message format).
 *  `content` is typed loose because upstream LLM providers (anthropic /
 *  openai-codex / pi-native) all serialize blocks slightly differently —
 *  `normalizeMessageContent` reconciles them. */
export interface ChatEventPayloadRaw {
  sessionKey: string;
  runId: string;
  seq: number;
  state: ChatEventState;
  message?: {
    role: string;
    content: unknown;
    timestamp: number;
    /** ⚠ The live frame nests the B2 discriminator HERE, while the persisted
     *  row carries it at the TOP level (`ingest-chat-lines.ts`: the
     *  `broadcastChat` payload vs the `appendCaptured` row). Two shapes for one
     *  contract, so `normalizeChatEvent` reads both. */
    model?: string;
    provider?: string;
  };
  errorMessage?: string;
  stopReason?: string;
  usage?: Record<string, unknown>;
  /**
   * B6→F3: present on the deterministic FALLBACK line the backend emits when
   * an armed reaction turn errors or times out. Names the `reactionRunId` this
   * line stands in for, so the SPA can clear the progress state it keyed to
   * that run. The line itself carries its ordinary `lexiun-ingest:*` runId.
   */
  replacesRunId?: string;
  /** B2 discriminator on the LIVE frame (see `lib/chat-receipt.ts`). */
  model?: string;
  provider?: string;
}

/** Normalized chat event for LexIDE consumption. Carries the full
 *  block array (no longer a flattened text string) so the SPA can
 *  render rich content (tool_use, thinking, image, etc.) as it streams.
 *  See ai-docs/lexiun-chat-content-model-plan.md §2. */
export interface ChatEventPayload {
  sessionKey: string;
  runId: string;
  state: ChatEventState;
  /** Normalized message blocks — empty array when the event carries no
   *  content (e.g. an `aborted` event). */
  blocks: ContentBlock[];
  error?: string;
  /** B2 discriminator, passed through verbatim from the raw frame. */
  model?: string;
  provider?: string;
}

/** Adapt raw gateway chat event to normalized typed-blocks format. */
export function normalizeChatEvent(raw: ChatEventPayloadRaw): ChatEventPayload {
  const blocks = raw.message?.content
    ? normalizeMessageContent(raw.message.content)
    : [];
  return {
    sessionKey: raw.sessionKey,
    runId: raw.runId,
    state: raw.state,
    blocks,
    error: raw.errorMessage,
    // Top-level first (the persisted-row shape), then nested (the live-frame
    // shape). Reading only the top level made the B2 primary key silently
    // inert on LIVE frames — and the `lexiun-ingest:` fallback matched anyway,
    // so receipts still rendered correctly and the gap was invisible. A
    // redundant path masking a broken one is not a passing test.
    model: raw.model ?? raw.message?.model,
    provider: raw.provider ?? raw.message?.provider,
  };
}

/**
 * Matches the runtime shape emitted by
 * `lexruntime/openclaw/src/infra/agent-events.ts` and broadcast by
 * `server-chat.ts` as `agent` / `session.tool` EventFrames. Tool
 * fields live under `data.*` — NOT at the top level. An earlier
 * version of this type put them at the top level and L.6.2's
 * `useFileOpenFromAgent` inherited the mistake; see that file's
 * header for the rationale.
 */
export interface AgentEventPayload {
  runId: string;
  seq: number;
  stream: string;
  ts: number;
  sessionKey?: string;
  data?: {
    phase?: AgentEventPhase;
    toolName?: string;
    toolInput?: Record<string, unknown>;
    toolResult?: unknown;
    [key: string]: unknown;
  };
}

export interface TaskEventPayload {
  taskId: string;
  state: TaskEventState;
  progress?: number;
  step?: string;
  output?: Record<string, unknown>;
  error?: string;
}

export type EventFrame =
  | { event: "chat"; payload: ChatEventPayload }
  | { event: "agent"; payload: AgentEventPayload }
  | { event: "task"; payload: TaskEventPayload }
  | { event: "session.tool"; payload: AgentEventPayload };

/* ---------- RPC request/response types ---------- */

export interface RpcRequest {
  id: string;
  method: string;
  params: Record<string, unknown>;
}

export interface RpcResponse<T = unknown> {
  id: string;
  result?: T;
  error?: RpcError;
}

export interface RpcError {
  code: number;
  message: string;
  details?: Record<string, unknown>;
}

/* ---------- Chat RPC ---------- */

/** Attachments travel alongside a chat message — the server-side
 *  `before_prompt_build` hook reshapes them into the structured
 *  preamble the LLM sees (plan §7.1 `<selected_range>` /
 *  `<current_document>` blocks). Phase 5.4.b introduces the client
 *  payload shape; 5.4.c wires the server rewrite. */
export interface ChatAttachmentPetitionSelection {
  type: "petition-selection";
  mimeType: "text/plain";
  content: string;
  metadata: {
    docId: string;
    versionHash: string;
    startLine: number;
    endLine: number;
  };
}

export type ChatAttachment = ChatAttachmentPetitionSelection;

export interface ChatSendParams {
  sessionKey: string;
  message: string;
  idempotencyKey?: string;
  attachments?: ChatAttachment[];
}

export interface ChatSendResult {
  runId: string;
  status: "ok" | "queued";
}

export interface ChatAbortParams {
  sessionKey: string;
  runId?: string;
}

export interface ChatAbortResult {
  ok: boolean;
  aborted: boolean;
}

export interface ChatHistoryParams {
  sessionKey: string;
}

/** Chat message — `content` is always a typed block array (post-§0
 *  wipe; no legacy string shape per the chat-content-model redesign).
 *  See ai-docs/lexiun-chat-content-model-plan.md §2. */
export interface ChatMessage {
  role: "user" | "assistant" | "system";
  content: ContentBlock[];
  timestamp: string;
  runId?: string;
  /** Producing model, when the row records one. Carries B2's
   *  `"gateway-injected"` marker for runtime-injected receipts — see
   *  `lib/chat-receipt.ts` for why this is a POSITIVE signal and why an
   *  absent value must never be read as "deterministic line". */
  model?: string;
  /** Producing provider; `"openclaw"` on gateway-injected rows. */
  provider?: string;
  /** Durable text-selection echo — the source file + selected text the prompt
   *  was sent WITH. Server-persisted (lx.chat_history) so it survives reload,
   *  superseding the in-memory `useSentSelectionStore` for replayed messages. */
  selection?: { source: string; text: string };
  /** Referenced workspace files the prompt was sent WITH (composer
   *  @-mention / drag-drop). Stamped LIVE by `addUserMessage` from the
   *  `runtime.system.enqueue_event` directive, AND re-hydrated on reload by
   *  `useChatSession`'s history loader, which parses the folded
   *  `<user_referenced_files>` block back out of the persisted turn text via
   *  `parseReferencedFilesFromHistory` (the runtime leaves the block in
   *  `m.text`; render-time `stripUserPreamble` hides it from display). So the
   *  echo chips survive reload, parity with the selection echo. */
  mentions?: { path: string; name: string }[];
  /** Conversation attachments the turn was sent WITH (e.g. the audio file an IM
   *  voice-note transcript came from). Rehydrated from `lx.chat_history`; the
   *  bytes are fetched on demand via `lx.chat_read_media` using `fetch` (the
   *  scope params are pre-bound by the history loader, which knows the scope). */
  attachments?: { name: string; mime?: string; fetch: LxChatReadMediaParams }[];
  /** Promptbox voice dictation (operator brief 2026-07-02): the turn was
   *  auto-submitted from a mic recording. Renders the same voice-note bubble
   *  as IM audio turns. `audioUrl` is a SESSION-LOCAL object URL of the
   *  recorded clip for immediate playback — LIVE-only (the dictation clip is
   *  not persisted runtime-side yet; on reload the turn degrades to plain
   *  text — durable stamping is a runtime-agent follow-up in the STT relay). */
  voice?: { audioUrl?: string };
  /**
   * How long the concierge WORKED before it started composing this answer —
   * run-start → the LATEST answer-start (miniplan §3; re-frozen when a tool
   * fires mid-answer, so it measures the same composition the runtime's
   * `model_call_started` stamp measures).
   *
   * ⚠ **Absent means "never observed", and absence renders NOTHING** — never
   * "0 s", never a substituted run-end figure. A run-end number would include
   * the answer-streaming tail and so render LARGER than the value the lawyer
   * watched freeze live, breaking the very parity the field exists for. An
   * aborted or errored turn never reaches an answer-start and therefore
   * carries no duration at all.
   *
   * Stamped CLIENT-side by `finalizeRun` today (L0+L1 is live-session-only).
   * L2 persists the same field on `v:2` rows, so the reload slice adds a
   * SOURCE for this value rather than a second renderer.
   */
  workedMs?: number;
  /** The turn's persisted activity trail, present only on rows the history
   *  loader read as trail-bearing (`v >= 2`). Drives the reload render; the
   *  LIVE path uses `content`'s leading `tool_use` blocks instead. */
  toolTrail?: ToolTrailEntry[];
  /** Mirrors the persisted row's `kind` (see `LxConversationMessage.kind`). */
  kind?: "no_answer" | "cancelled";
  /** Mirrors the persisted row's `cause`. Absent ⇒ neutral wording. */
  cause?: "overflow";
  /** Measured per-step outcomes, captured from the live turn at finalize.
   *  CLIENT-SESSION-ONLY — never persisted; a reload renders from the trail's
   *  own outcomes instead. Exists because discarding an observed ✓ at finalize
   *  would render a step "unknown" for the rest of the session while the very
   *  same step shows ✓ after a reload — the dishonest direction. */
  outcomes?: Record<string, "ok" | "error">;
  /** ⚖ A-11 — per-step provenance for a LIVE-finalized turn, keyed by the
   *  `tool_use` block id. CLIENT-SESSION-ONLY, merged at turn end by the
   *  hydration pass (`hydrateTurnAttribution`) from the persisted v>=3 row —
   *  the live wire cannot carry provider (D5 strips results; meta is built
   *  from args at call start). A reloaded turn never uses this: its trail
   *  entries carry `provider` directly. Absent entry ⇒ no badge. */
  trailAttribution?: Record<string, { provider: string; path?: string }>;
}

export interface ChatHistoryResult {
  messages: ChatMessage[];
}

/* ---------- lx.chat_history (Lexiun-owned conversation store) -----------
 * The reload-survival read API (ai-docs/lexiun-conversation-history-plan.md).
 * Core chat.history is a lossy agent-context DAG that drops text-only
 * assistant replies; this returns the FULL un-pruned thread for a scope.
 * `text` is already-rendered plain text (not a content-block array). */
/** A file attached to a persisted conversation turn (e.g. the audio behind an
 *  IM voice-note transcript). `path` is the runtime-local durable copy — the
 *  SPA never reads it directly; bytes are served via `lx.chat_read_media`. */
export interface LxConversationAttachment {
  kind: "file";
  path: string;
  name: string;
  mime?: string;
  sizeBytes?: number;
}
/**
 * One step of a turn's persisted activity trail (L2 / D2), exactly as the
 * runtime writes it — `ai-docs/concierge-live-activity-trail-l2-miniplan.md` §2.
 *
 * ⚠ Deliberately carries NO raw args and NO results (D5). That is why the
 * arg-driven live renderers (`ReadBadge` wants `input.path`, `EditBlock` a diff)
 * can never run on these, and why the reload path renders its own compact row
 * instead of synthesising fake `tool_use` blocks — doing that would mean
 * re-inventing at read time the structure the writer refused to store. See
 * `ai-docs/lexide-live-activity-reload-parity-miniplan.md` §2.
 */
export interface ToolTrailEntry {
  /** Raw tool name, e.g. "lx_read". Always present — the stable key. */
  name: string;
  /** Friendly label. ABSENT ⇒ the renderer shows `name`; never invented.
   *  (Measured 2026-08-13: absent on every live entry, so `name` is the norm.) */
  label?: string;
  /** Short human target (path/alias/query), truncated to 120 at the WRITER. */
  target?: string;
  /** REQUIRED. `"unknown"` is a real member, not a filler — an unobserved
   *  result must never render as success. */
  outcome: "ok" | "error" | "unknown";
  /** ⚖ A-11 — WHO served this call, copied VERBATIM by the writer from the
   *  tool's own result `details` (never re-resolved from config — the A-12
   *  attribution-lands-once ruling). ABSENT ⇒ the result named no provider,
   *  which for a non-web tool is the normal case. ⚠ `"http"` is a SENTINEL
   *  meaning "no provider involved — the runtime fetched the URL itself";
   *  it is never a member of the A-10/A-11 provider ladder and renders as a
   *  localized "direct access" wording, never the raw string.
   *  See ai-docs/a11-provider-badge-emit-miniplan.md §2/§3. */
  provider?: string;
  /** ⚖ A-11 — HOW it was served, when (and only when) the tool emits one
   *  (today only `lx_browser_fetch`, value `"browser"`). Never synthesized. */
  path?: string;
}

export interface LxConversationMessage {
  role: "user" | "assistant";
  text: string;
  ts: number;
  runId: string;
  tools?: string[];
  /**
   * Schema version of THIS row. `2` ⇒ the row carries `toolTrail`.
   * `3` ⇒ additionally, trail entries MAY carry `provider`/`path` (A-11) —
   * so under `v >= 3` an ABSENT `provider` is a MEASUREMENT ("the result
   * named none"), while under `v: 2` it is IGNORANCE ("the writer could not
   * have known"). Both render no badge; the distinction keeps the store
   * honest for diagnostics (and: a v>=3 `web_fetch` entry ALWAYS carries
   * `provider` — `"http"` at minimum — so a blank one is a bug signal).
   *
   * 🚨 **Readers switch on `v`, never on field presence.** Sniffing
   * `toolTrail !== undefined` is absence-as-signal, which produced three
   * misdiagnoses in this store on 2026-08-02/03. A row is v:1 unless it says
   * otherwise — including a v:1 row that somehow carries a trail.
   */
  v?: 1 | 2 | 3;
  /**
   * The turn's activity trail — the lossless SIBLING of `tools[]`.
   *
   * 🔴 `tools[]` and `toolTrail` come from DIFFERENT SOURCES and CAN DISAGREE:
   * the trail is accumulated live from `before/after_tool_call`, while `tools[]`
   * is a legacy `agent_end` name-walk. Measured on one live row: `tools` = 8
   * against a 32-entry trail. **Render v:2 rows from `toolTrail` ONLY and never
   * cross-validate the two** — a mismatch is not a bug to reconcile.
   */
  toolTrail?: ToolTrailEntry[];
  /**
   * Run-start → answer-start, stamped runtime-side.
   * ⚠ ABSENT means never observed ⇒ render NO duration. Never zero, never a
   * substitute (a run-end figure would include the answer-streaming tail and
   * render LARGER than what the lawyer watched freeze live).
   */
  workedMs?: number;
  /** Final plan-tool (lx_todo/update_plan) state of the turn — the TodoCard
   *  re-renders from this on history replay (rich-history format). */
  planTool?: { name: string; input: unknown };
  /** Structured text-selection the turn was sent with (the durable echo home). */
  context?: { kind: "selection"; sourceFile: string; text: string };
  /** Files the turn was sent with (IM audio → the .ogg the transcript came from). */
  attachments?: LxConversationAttachment[];
  /**
   * Row kind discriminator (7802ab6d0 + 54843fbde).
   *
   * `"no_answer"` — the turn's agent chain died without ever producing an
   * answer; written ONCE by the runtime's settle sweep (~10 min after death),
   * never superseded.
   * `"cancelled"` (R6b) — the lawyer stopped their own MID-RUN turn. An act,
   * not a fault: renders as a light interrupted state, NEVER an error card.
   * (The pre-run-start cancel window persists nothing until R6c — expect
   * silence there, not a row.)
   *
   * `text` is EMPTY by design on kind rows; presentation is the SPA's.
   * 🚨 Switch on the VALUE — a healthy intermediate compaction segment ALSO
   * ends with empty text, so emptiness is precisely the wrong discriminator.
   */
  kind?: "no_answer" | "cancelled";
  /** Why the chain died. ABSENT today (ships when something actually observes
   *  a reason) — an absent cause renders the NEUTRAL text, never a confident
   *  one. */
  cause?: "overflow";
  /** Producing model/provider as PERSISTED on the row. Optional because the
   *  runtime stamps them only from B2 onward — every row written before that
   *  lacks both, which is exactly why `lib/chat-receipt.ts` keeps the
   *  `lexiun-ingest:` runId fallback and must never read an absent `model` as
   *  a signal in its own right. */
  model?: string;
  provider?: string;
}
export interface LxChatHistoryParams {
  /** "global"/"org" → the per-user org thread; omit (+ pass ids) for a case thread. */
  scope?: "global" | "org";
  clientId?: string;
  matterId?: string;
  caseId?: string;
  /** Most-recent-N page. Either `limit` or `before` selects the BOUNDED read
   *  path server-side (`3168dd1a8`); omitting both is the legacy full read,
   *  which cannot report `readError` and re-parses the whole thread. */
  limit?: number;
  /** Opaque cursor from a previous response — returns the block strictly
   *  BEFORE it (backward pagination). A bad/foreign cursor is INVALID_PARAMS,
   *  never a silent tail: surface it, do not retry bare. */
  before?: string;
}
export interface LxChatHistoryResult {
  messages: LxConversationMessage[];
  /** Whether at least one OLDER message exists before this block. MEASURED
   *  server-side (limit+1 fetch), never inferred — trust it. Optional only
   *  for tolerance of a pre-paging runtime; absent reads as false. */
  hasMore?: boolean;
  /** Pass as `before` to fetch the preceding block. Absent when the page is
   *  empty. */
  cursor?: string;
  /** THIRD STATE — the read FAILED. An empty `messages` WITHOUT this means
   *  "no history"; WITH it means "we could not look". Rendering the second as
   *  the first shows a lawyer an empty case that is not empty. Populated on
   *  the PAGED path only (the full read still swallows errors → `[]`). */
  readError?: string;
}
/** `lx.chat_read_media` — serve a durable conversation-attachment file. Scope
 *  params mirror LxChatHistoryParams (same session-key derivation server-side). */
export interface LxChatReadMediaParams {
  scope?: "global" | "org";
  clientId?: string;
  matterId?: string;
  caseId?: string;
  /** The attachment's file name (basename — sanitized server-side). */
  name: string;
}
export interface LxChatReadMediaResult {
  name: string;
  mime: string;
  bytesBase64: string;
  sizeBytes: number;
}

/* ---------- runtime.llm_status (read-only model-selection visibility) ----
 * LLM model-selection visibility Phase 1 (ai-docs/llm-provider-abstraction
 * -plan.md). Read-only for ALL users (D2). Mirrors the plugin contract in
 * extensions/lexiun/src/gateway-methods/llm-status.ts. `tier:"A"` =
 * platform-locked (never editable); `tier:"B"` = future org-admin-editable
 * (Phase 2 flips `capabilities.canEdit`). */
export interface LlmProfileStatus {
  name: string;
  tier: "A" | "B";
  locked: boolean;
  editable: boolean;
  criteria?: unknown;
  resolved?: {
    provider: string;
    model: string;
    policy: {
      thinkingDefault: string;
      maxOutputTokens: number | null;
      temperature: number | null;
      languageHint?: string;
    };
  };
  unresolvable?: boolean;
  code?: string;
  provenance?: string;
}

export interface LlmAssignmentStatus {
  context: string;
  tier: "A" | "B";
  locked: boolean;
  editable: boolean;
  profile: string;
  scopeNote?: string;
  narrow?: { provider?: string };
  provenance: string;
}

export interface RuntimeLlmStatus {
  meta: { generatedAt: string; schemaVersion: number };
  capabilities: {
    canEdit: boolean;
    editRoles: string[];
    editableTiers: string[];
    platformDefaultEditRole: string;
  };
  providers: string[];
  profiles: LlmProfileStatus[];
  assignments: LlmAssignmentStatus[];
  effectiveDefault: {
    tier: "A" | "B";
    locked: boolean;
    editable: boolean;
    model: string | null;
    thinkingDefault: string | null;
    source: string;
    provenance: string;
    precedence: string[];
  } | null;
  heartbeat: {
    tier: "A" | "B";
    locked: boolean;
    editable: boolean;
    agentId: string;
    enabled: boolean;
    every: string | null;
    model: string | null;
    source: string;
    provenance: string;
    docRef: string;
  } | null;
  platformLocked?: { tier: "A"; locked: true; items: string[] };
  /** Set when the plugin degraded (catastrophic read error) — UI shows
   *  "unavailable" rather than an error. */
  degraded?: boolean;
}

/* ---------- Client RPC (D5b) ---------- */

export interface ClientOpenParams {
  clientId: string;
}

export interface ClientOpenResult {
  clientId: string;
  sessionKey: string;
  name: string;
  orgId: string;
  isPerson: boolean;
  isSelfClient: boolean;
  status: string;
  matterCount: number;
}

export interface ClientCloseParams {
  clientId: string;
}

export interface ClientContextParams {
  clientId: string;
}

export interface ClientContextResult {
  clientId: string;
  name: string;
  isPerson: boolean;
  isSelfClient: boolean;
  status: string;
  matterCount: number;
  cpfCnpj?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface ClientSetStatusParams {
  clientId: string;
  status: string;
}

export interface ClientSetStatusResult {
  ok: true;
  status: string;
  prevStatus?: string;
  unchanged?: boolean;
}

export interface ClientUpdateParams {
  clientId: string;
  /** New full/legal name (rename); omit to leave unchanged. */
  name?: string;
  /** Arbitrary text; the server COERCES it to a `@handle` + dedupes per org.
   *  Empty string clears the alias; omit to leave it unchanged. */
  alias?: string;
}

export interface ClientUpdateResult {
  ok: true;
  clientId: string;
  name: string;
  /** The FINAL server-normalized alias — MAY differ from what was sent
   *  (coerced to the `@handle` grammar, deduped per org). */
  alias?: string;
  /** Which fields actually moved. */
  changed?: ("name" | "alias")[];
  /** Set when nothing changed (no-op). */
  unchanged?: boolean;
}

export interface ClientDeleteParams {
  clientId: string;
  /** Remove the (non-empty) workspace folder with the client. The ruled
   *  delete removes everything, so the SPA always sends true — omitting it is
   *  no longer fatal (ce39526ac) but orphans the folder on disk. */
  force?: boolean;
}

/**
 * `client.delete` result — the 2026-07-30 v2 ruling, wire shape CONFIRMED by
 * the P2 runtime handback (commit ac2ec7f3f; the `case.refresh` convention:
 * a business refusal is a typed code on `ok:false`, the transport error
 * envelope stays transport-only). Success means the client row + its
 * (provably empty, post-unassign) matter rows + the runtime workspace folder
 * are GONE and every referencing case was unassigned to «Não atribuídos».
 * `CONFLICT` = legacy-`storageShape` cases block; `FORBIDDEN` = self-client.
 * `details` is lexserver's structured payload forwarded verbatim.
 */
export type ClientDeleteResult =
  /** `deleted` is the ONLY success signal (registry committed). The workspace
   *  fields report the best-effort folder removal SEPARATELY — a removal
   *  failure surfaces BESIDE success, never folded into it (ce39526ac);
   *  consumers key off `deleted` and at most log the workspace fields. */
  | { ok: true; deleted: true; workspaceRemoved?: boolean; workspaceError?: string }
  | {
      ok: false;
      code: "CONFLICT" | "FORBIDDEN";
      message?: string;
      /** On CONFLICT: the blocking legacy-shape cases, for the ⛔ screen. */
      cases?: { caseId: string; title: string }[];
      details?: unknown;
    };

export interface MatterUpdateParams {
  clientId: string;
  matterId: string;
  /** The new localized display label. displayNames-only (entity-identity
   *  INV-3): NEVER touches slug/matterId/on-disk path. A `pt` rename also
   *  mirrors the primary `name` fallback (lexserver's own contract). */
  name: string;
  /** Which localized label to write. Defaults to "pt" runtime-side. */
  locale?: "pt" | "en" | "es";
}

/** ⚠ `unchanged: true` = same label for that locale, silent no-op — render it
 *  as saved-nothing-to-do, NOT as a failure (P2 handback's explicit warning). */
export interface MatterUpdateResult {
  ok: true;
  unchanged?: boolean;
}

export interface ClientCreateParams {
  name: string;
  /** The creating user's id (becomes the client owner/LEAD). */
  createdBy: string;
  /** Individual person vs company/org. Default false = company. */
  isPerson?: boolean;
}

export interface ClientCreateResult {
  clientId: string;
  /** The new client's session key (runtime scope handle). */
  sessionKey: string;
  name: string;
  orgId: string;
  isPerson: boolean;
  isSelfClient: boolean;
  /** Always "active" for a fresh client. */
  status: string;
}


/* ---------- Matter RPC (D5b) ---------- */

export interface MatterOpenParams {
  clientId: string;
  matterId: string;
}

export interface MatterOpenResult {
  clientId: string;
  matterId: string;
  sessionKey: string;
  name: string;
  matterType: string;
  status: string;
  caseCount: number;
  /** Entity-identity Slice 1 (2026-07-08): the immutable path `slug`, the i18n
   *  `displayNames` (render `displayNames[locale] ?? name`), and the internal
   *  English `description`. Optional — legacy matters lack them until the §8
   *  migration; the runtime returns them from the disk model NOW. */
  slug?: string;
  displayNames?: DisplayNames;
  description?: string;
}

export interface MatterCloseParams {
  clientId: string;
  matterId: string;
}

export interface MatterContextParams {
  clientId: string;
  matterId: string;
}

export interface MatterContextResult {
  clientId: string;
  matterId: string;
  name: string;
  matterType: string;
  status: string;
  caseCount: number;
  /** Entity-identity Slice 1 — see MatterOpenResult. Optional/fail-soft. */
  slug?: string;
  displayNames?: DisplayNames;
  description?: string;
  /** Case folder ids under this matter (runtime returns via registry.listCases). */
  caseIds: string[];
  /**
   * User-facing label for this matter's case-level unit ("Caso" /
   * "Contrato" / "Consulta" / ...). NOT populated yet — D8b adds the
   * runtime-side handler edit + lexide consumer. The optional field
   * is declared here so future consumers can reference it without a
   * type refactor when the runtime starts returning it.
   */
  caseDisplayLabel?: {
    singular: { pt: string; en: string };
    plural: { pt: string; en: string };
  };
}

/* ---------- Case RPC ---------- */
//
// D5a: every case-scoped RPC now requires `clientId` + `matterId`
// per the post-Phase-C runtime contract (see lexide-broadening-plan.md
// §C-Audit-1). `case.open` additionally accepts `userId` + `orgId`
// which the runtime uses to resolve the caller's cascade role via
// the C10d cascade resolver. When both are omitted the runtime drops
// the `role` field from the response (no silent OBSERVER fallback).

export interface CaseOpenParams {
  /** OPTIONAL (flat storage, D-8): a flat/unassigned case opens by `caseId` alone —
   *  the runtime resolves the on-disk location from the registry. Send both parents
   *  when known (an assigned case); omit for an assignment-pending case. */
  clientId?: string;
  matterId?: string;
  caseId: string;
  /** Required for cascade role resolution; when omitted `role` is absent in the response. */
  userId?: string;
  /** Required for cascade role resolution; when omitted `role` is absent in the response. */
  orgId?: string;
}

/**
 * What the VIEWER may do on a case. Mirrors the runtime's `viewerPermissions`
 * (`gateway-methods/case.ts`) — the THREE fields that wire carries, each
 * individually optional: a field the server could not answer is ABSENT, and
 * **absent ≠ false**.
 *
 * ⚠ **Deliberately NOT `types/access.ts`'s `CasePermissions`**, and do not
 * "unify" them. That one is ten REQUIRED booleans, so it cannot represent
 * *unknown* at all — every field is forced to true or false. This contract is
 * built on absent-means-unknown, so a required-boolean shape would erase the
 * distinction the moment a payload was coerced into it.
 */
/**
 * ⚖ **D-64 / D-67 (§6.6.5.2) — a DISCRIMINATED UNION, and the shape is the point.**
 *
 * `resolution: "unresolved"` carries **no boolean fields at all**, deliberately:
 * the runtime built it that way so `perms.can_write` is *unavailable* without
 * first narrowing on `resolution`, making the wrong read a COMPILE error rather
 * than a silent `undefined` (`case.ts:1228-1234`).
 *
 * ⚠ **Do not "simplify" this back into three optional booleans.** It was exactly
 * that shape here until 2026-08-10, which compiled fine, read `undefined` on an
 * unresolved case, and threw away the entire protection the runtime paid for.
 * Three optional booleans cannot distinguish *"the server said no"* from *"the
 * server could not tell"* — and those are different facts about a lawyer's case.
 *
 * The object itself is ALWAYS PRESENT on a supporting runtime (the §6.6.5.1 gate
 * row). Its ABSENCE therefore means *old build*, which is a third thing again —
 * see `lib/case-permissions.ts`, the only file allowed to read this.
 */
export type ViewerPermissions =
  | {
      resolution: "resolved";
      /** ⚠ The full flag set the runtime SERVES, measured on the live wire
       *  2026-08-10. An earlier version of this type declared only three
       *  (`can_read`/`can_write`/`can_view_sensitive`) — the other seven were
       *  arriving and were simply unmodelled, so `permissionState(perms,
       *  "can_delete")` was not even expressible. Under-declaring an inbound
       *  shape does not make the fields absent; it makes them unusable and
       *  invisible, which reads as "the server does not send that" to the next
       *  person who looks. Cross-checked against the runtime's own
       *  `RequiredFlag` union (`access/tool-flag-matrix.ts`). */
      can_read?: boolean;
      can_write?: boolean;
      can_delete?: boolean;
      can_draft?: boolean;
      can_file?: boolean;
      can_use_ai?: boolean;
      can_view_sensitive?: boolean;
      /** Observed on the wire, outside the tool-flag matrix — the runtime's
       *  own comment calls `can_view_costs`/`can_assign` additional flags. */
      can_view?: boolean;
      can_assign?: boolean;
      can_view_costs?: boolean;
    }
  | { resolution: "unresolved" };

export interface CaseOpenResult {
  sessionKey: string;
  /** Present-but-NULLABLE (D-8, flat storage): the parent chain resolved by the
   *  runtime (null ⇒ assignment-pending / client-only — render "Unassigned"). A
   *  flat case is opened by caseId alone, so both may be null. */
  clientId: string | null;
  matterId: string | null;
  title: string;
  /**
   * Cascade-resolved role, present only when caller passed `userId`+`orgId`
   * AND the resolver found a grant. NEVER fabricate a value when absent —
   * display "—" or "Role unresolved" and disable role-gated affordances.
   */
  role?: string;
  /** Where the cascade resolver found the grant (emitted alongside `role`; absent
   *  when `role` is). Informational — mirror of the wire, not consumed today. */
  resolvedFrom?: string;
  /**
   * ⚖ D-64 — what **THE VIEWER** (this lawyer, this session) may do, resolved
   * from the RUNTIME PRINCIPAL. Landed runtime-side as `e6c0ff88b`.
   *
   * ⚠ **Do NOT derive affordances from `role` above.** `role` resolves from
   * `params.userId` — CLIENT INPUT — because LexPortal uses it to ask *"what is
   * USER X's role on this case"*. The two fields can therefore be truthful
   * answers ABOUT DIFFERENT PEOPLE in one payload. The name says whose.
   *
   * ⚠ **INFORMATIONAL ONLY — this is an AFFORDANCE input, never enforcement.**
   * LexIDE runs in the lawyer's browser; the 2026-08-06 probe drove the gateway
   * directly and never used the SPA, so a check here is a check the caller
   * controls. Enforcement is `user-management-concept.md` §6.6.3, server-side.
   * A stale or absent value is a UX defect, never a security one. A later
   * "simplification" must not trust it.
   *
   * ⚠ **The safe default here INVERTS the server's** (§6.6.5.1): an enforcement
   * layer DENIES what it cannot resolve; an affordance layer PERMITS it. That
   * is safe only because the gate refuses independently — if this were ever the
   * enforcement, that rule would BE the hole. Read `lib/case-permissions.ts`
   * before "harmonising" the two.
   */
  viewerPermissions?: ViewerPermissions;
  /** D5a: denormalized matterType from the parent Matter. Drives matterType-aware tab registry. */
  matterType?: string;
  parties: import("./party").Party[];
  status: string;
  /**
   * Capability tokens the runtime positively advertises for this case — e.g.
   * `["case.refresh"]`. **OMITTED entirely when nothing is advertised**, which
   * is every runtime before the marker shipped; absence therefore means "no
   * capability", never "assume yes".
   *
   * It rides `case.open` rather than `case.context` deliberately: `case.open`
   * is on the data path `useCaseWorkspace` already fetches, so the fact arrives
   * where its consumer already looks. The first attempt put it on
   * `case.context`, which LexIDE has never called — the marker shipped through
   * a successful roll and reached nobody (ruled 2026-07-29).
   */
  capabilities?: string[];
}

/* ---------- case.refresh (contract §18.5 — the ⟳ Atualizar force path) ---------- */

export interface CaseRefreshParams {
  caseId: string;
}

/**
 * FOUR outcomes, and the UI must tell them apart.
 *
 * - `opened`    — the case drifted; a `m-case-updated` mission was started.
 * - `unchanged` — nothing drifted, so NO mission was opened. A legitimate,
 *                 common, non-error result: §18.5's force skips the WAIT, not
 *                 the CHECK.
 * - `debounced` — suppressed by the §18.4 window.
 * - `failed`    — the refresh could not run (e.g. no resolvable topology).
 */
export type CaseRefreshStatus = "opened" | "unchanged" | "debounced" | "failed";

/**
 * ⚠ **The gateway answers `ok: true` even when `status` is `failed`** — by
 * design: a business outcome is not a transport error, and routing it through
 * the error envelope would make the SPA unable to tell it from a dropped
 * socket. So a `.catch` NEVER sees a failed refresh. **Switch on `status`.**
 *
 * This is the same shape as the D4 defect one layer up: the failure path
 * RESOLVES, so a consumer guarding only the rejection arm reports failure as
 * success.
 */
export interface CaseRefreshResult {
  status: CaseRefreshStatus;
  /** Human sentence from the runtime, already localized and shared verbatim
   *  with the concierge tool's wording (one definition, so the button and the
   *  chat answer cannot drift). Tool-protocol error prefix already stripped. */
  message?: string;
  /** Present on `opened`. */
  missionId?: string;
  revision?: number;
  /** Present on `failed`. */
  reason?: string;
}

/** D5a: new short-URL bootstrap RPC. Takes a bare caseId and returns parent ids. */
export interface CaseLocateParams {
  caseId: string;
}

export interface CaseLocateResult {
  caseId: string;
  /** Present-but-NULLABLE (D-8, flat storage): `null` ⇒ the case is
   *  assignment-pending / client-only — render "Unassigned", never assume set. */
  clientId: string | null;
  matterId: string | null;
  orgId: string;
}

export interface CaseCloseParams {
  /** OPTIONAL (flat storage): the runtime closes by `caseId` alone (layout-agnostic). */
  clientId?: string;
  matterId?: string;
  caseId: string;
}

/* ---------- case.create (New-Case UI — workspace-keeper create-mode) ----------
 *
 * Scaffolds a case (six-folder skeleton + FOLDERS.md/folders.json +
 * CASE_MODEL.json) via the runtime's workspace-keeper create-mode, then
 * registers it — a claim-then-delegate + rollback flow. Shipped runtime-side
 * 2026-07-05 (workspace-keeper-plan.md §TIER-1 v2). Called through the same
 * lexserver→runtime WS relay as `case.workspace_tree`; no new lexserver route.
 * Handoff: ai-docs/handoffs/case-creation-lexide-handoff-2026-07-05.md. */
export interface CaseCreateParty {
  name: string;
  role?: string;
}

export interface CaseCreateParams {
  /* Resolve-or-create the client→matter chain (operator 2026-07-06). The user
   * specifies a client and a matter upfront; each is pick-an-existing (by id) OR
   * type-a-new-name (auto-created + deduped server-side). Handoff:
   * ai-docs/handoffs/case-create-resolve-or-create-chain-handoff-2026-07-06.md.
   *
   * "Create now, assign later" (runtime handback §3): OMIT all four of
   * clientId/clientName/matterId/matterName for a TITLE-ONLY call — the case is
   * born unassigned (clientId/matterId null; status "intake") and lands in the
   * Unassigned bucket. A matter still requires its client (never sent alone). */
  /** Pick an EXISTING client by id (verbatim). Omit entirely for a title-only
   *  ("assign later") case; else one of clientId|clientName. */
  clientId?: string;
  /** Type a NEW-or-existing client by name (folded + deduped server-side). */
  clientName?: string;
  /** NEW-client attribute only (ignored on reuse). Default false = company/org. */
  isPerson?: boolean;
  /** Pick an EXISTING matter by id (must belong to the resolved client). One of
   *  matterId|matterName required. */
  matterId?: string;
  /** Type a NEW-or-existing matter by name (folded within the resolved client). */
  matterName?: string;
  /** NEW-matter attribute only — one of the NINE runtime matter types (the 6→9
   *  widen, 2026-07-08): civil|labor|tax|contract|advisory|corporate|realestate|
   *  compliance|other. Source of truth = NewCaseDialog `RUNTIME_MATTER_TYPES`.
   *  Ignored on reuse. */
  matterType?: string;
  title: string;
  /** The current user's id (the case owner/creator). */
  createdBy: string;
  /** Optional parties — feed the advisory conflict-of-interest scan. */
  parties?: CaseCreateParty[];
  /** Optional — the runtime generates one when omitted (do NOT ask the user). */
  caseId?: string;
  /** ⚖ **D-98** — authorise creating the named client/esfera that does not exist
   *  yet. Absent/false ⇒ an unresolved parent name is REFUSED with the gateway's
   *  near-miss "did you mean …", which is the typo protection D-70(c) exists to
   *  provide (`resolve-parents.ts:243`, `:368`).
   *
   *  ⚠ **Send `true` ONLY for an explicit create act** — `isExplicitCreate()` on
   *  the dialog's `NameOrPickValue`, never on the mere presence of a name.
   *  Creating a client or esfera is a PERSISTENT org-wide write.
   *
   *  ⚠ **The name is `createParents`, not `createNewParents`.** The latter is the
   *  CONCIERGE TOOL's model-facing param (`lx-create-case.ts:91`), mapped onto
   *  this one at `index.ts:3377`. Two layers, two deliberate names — sending the
   *  tool's spelling here is silently ignored, which reads as "the fix did not
   *  work" rather than as a wrong field. */
  createParents?: boolean;
}

/** A conflict-of-interest hit — a proposed party matches a party on an existing
 *  case. Serialized shape from the runtime's `toConflictHitPayload` (single
 *  source of truth; mirrors lexserver's `ConflictHitPayloadSchema`). Advisory. */
export interface CaseConflictHit {
  proposedName: string;
  proposedRole: string | null;
  existingCaseId: string;
  existingClientId: string;
  existingMatterId: string;
  existingCaseTitle: string;
  existingRole: string | null;
}

export interface CaseCreateResult {
  caseId: string;
  /** NULL on the "create now, assign later" (title-only) path — the case is born
   *  unassigned and lands in the Unassigned bucket until a client/matter is
   *  attached. Non-null on the resolve-or-create chain path. */
  clientId: string | null;
  matterId: string | null;
  /** Immutable folder slug minted at create (flat storage) — `cases/{slug}--{uid}`.
   *  Written on EVERY new case (a legacy-born case also gets one for the eventual
   *  migration). Powers the "Rename case folder" (reslug) affordance. */
  slug: string;
  /** On-disk layout of the freshly-created case (D-4, no-fallbacks). */
  storageShape: "flat" | "legacy";
  /** The new case's session key (runtime scope handle). */
  sessionKey: string;
  title: string;
  /** Always "intake" for a fresh case. */
  status: string;
  /** Conflict-of-interest hits — empty/absent = clean. Advisory (case still created). */
  conflicts?: CaseConflictHit[];
  /** Resolve-or-create feedback (runtime dependency — SPA treats as OPTIONAL and
   *  fails soft to a plain toast if an older runtime omits them). `created`
   *  distinguishes an auto-created client/matter from a reused one so the UI can
   *  surface "Criou o cliente X + a matéria Y". */
  client?: { id: string; name: string; created: boolean };
  /** `id`/`name` are NULLABLE/absent on the client-without-matter shape (c): the
   *  case is born with a client but no matter, so the runtime emits `id: null`. */
  matter?: { id: string | null; name?: string; created: boolean };
}

/* ---------- case.reslug (deliberate, DRAIN-GATED folder rename) ----------
 *
 * A DELIBERATE, DRAIN-GATED rename of the case's on-disk workspace FOLDER
 * slug — DISTINCT from the everyday display-name rename. It DRAINS in-flight
 * work on the case before renaming the folder, so it is NEVER the default
 * rename path: expose it only as a rare, clearly-labeled exception-path
 * control whose confirmation WARNS the user it drains in-flight work.
 *
 * The `caseId` is the IMMUTABLE reference — it does NOT change, so the open
 * case view / URL survives the reslug; only the folder `slug` moves. After a
 * successful reslug the runtime emits `case.updated { kind:"reslugged",
 * caseId, slug, prevSlug }`, which the existing `useCaseUpdatedStream`
 * subscribers pick up to refresh — the SPA does not re-navigate.
 *
 * NEW gateway RPC — BUILDING runtime-side as S4 (runtime handback §4). Until
 * S4 lands the method 404s, so the SPA action must surface that error
 * gracefully (it never silently succeeds).
 *
 *   request:   case.reslug { caseId, newSlug? }   // newSlug omitted → re-mint
 *                                                  //   the slug from the title
 *   response:  { caseId, slug, prevSlug }          // caseId UNCHANGED
 *   broadcast: case.updated { kind:"reslugged", caseId, slug, prevSlug }
 */
export interface CaseReslugParams {
  /** The immutable case reference — the folder that gets re-slugged. */
  caseId: string;
  /** Omit → the runtime re-mints the slug from the case's current title.
   *  Provide to force a specific slug (the runtime still canonicalizes it). */
  newSlug?: string;
}

export interface CaseReslugResult {
  /** The immutable case reference — UNCHANGED by the reslug. */
  caseId: string;
  /** The new on-disk folder slug. */
  slug: string;
  /** The prior folder slug (drives the "renamed X → Y" confirmation toast). */
  prevSlug: string;
}

/* ---------- case.assign (D-5 deferred-assignment) ---------- */

/**
 * `case.assign` (D-5, S4a) — assign / reassign / unassign a FLAT case's parentage.
 * Metadata-only (rewrites the case's disk CASE_MODEL parentage + D-12 snapshot,
 * mirrors to the lexserver registry). A legacy (unmigrated) case is REFUSED
 * (`INVALID_PARAMS` — its folder path is bound to its original parents).
 *
 * The op is **id-based** — a name with no id is NOT resolve-or-created; a typed
 * brand-new client/matter must be created elsewhere first (the New-Case chain).
 * `clientName`/`matterName` are display-snapshot labels only.
 *
 * On success the runtime broadcasts
 * `case.updated { kind:"reassigned", caseId, clientId, matterId, prevClientId, prevMatterId }`.
 */
export interface CaseAssignParams {
  /** The immutable case to (re)assign. */
  caseId: string;
  /** Tri-state: a non-empty string SETS the client, explicit `null` CLEARS it
   *  (full unassign — also clears the matter), ABSENT LEAVES it unchanged.
   *  At least one of clientId/matterId must be present. */
  clientId?: string | null;
  /** Tri-state (see clientId). **Coherence (runtime-enforced):** assigning/keeping
   *  a matter requires BOTH clientId + matterId in the SAME call (a matter belongs
   *  to one client; the runtime does not look the client up). */
  matterId?: string | null;
  /** Display-snapshot label for the assigned client (D-12) — NOT resolve-or-create. */
  clientName?: string;
  /** Display-snapshot label for the assigned matter (D-12) — NOT resolve-or-create. */
  matterName?: string;
}

export interface CaseAssignResult {
  ok: boolean;
  /** The immutable case reference — UNCHANGED by the assign. */
  caseId: string;
  /** The resulting parentage after the assign (nullable — a full unassign clears both). */
  clientId: string | null;
  matterId: string | null;
  /** Present + true when the requested assignment matched the current state (a no-op —
   *  no registry PATCH / index rebuild / broadcast happened runtime-side). */
  unchanged?: boolean;
}

/* ---------- case.workspace_tree (CaseNavigator case-mode) ---------- */
//
// Real on-disk workspace folder/file tree, filtered server-side: hidden
// segments (names starting with `.`/`_`, plus `__pycache__`) and
// internal `*.meta.json` sidecars never cross the wire. Mirrors the
// plugin's `WorkspaceTreeFile`/`WorkspaceTreeFolder` contract
// (extensions/lexiun/src/gateway-methods/case-tree.ts).

export interface CaseWorkspaceTreeParams {
  /** OPTIONAL (flat storage): a flat/unassigned case's tree resolves by `caseId` alone
   *  via the runtime's `resolveGatewayWorkspace` (openCaseByLoc from the registry). */
  clientId?: string;
  matterId?: string;
  caseId: string;
}

/* ---------- Org-scope ("Global" per-user workspace) variants ----------
 *
 * CCM Wave 2 (org-pane handoff `ccm-lexide-org-pane-handoff-2026-06-28.md`):
 * the runtime gateway-methods (R3, lexruntime commit `9ed2d9e7`) accept
 * `{ scope: "org" }` and OMIT `clientId/matterId/caseId` — the owner is
 * resolved server-side from `LEX_USER_ID`. The org workspace is
 * byte-for-byte case-shaped on disk (same zone layout `00_inbox …
 * 08_monitoring .lexiun`), so the tree/viewer machinery binds with ZERO
 * folder-name special-casing — only the `{scope:"org"}` param differs.
 *
 * Each org-variant carries a literal `scope: "org"` discriminant so the
 * RPC client wrappers below stay typed end-to-end.
 */
export interface OrgWorkspaceTreeParams {
  scope: "org";
}

export interface WorkspaceTreeFile {
  /** Basename, e.g. "contestacao.md". */
  name: string;
  /** Case-root-relative (= zone-relative) path, e.g. "07_drafts/contestacao.md". */
  path: string;
  isDirectory: false;
  size?: number;
  /** ISO mtime. */
  modifiedAt?: string;
  /**
   * CCM copy/move §1.5 — origin chip data for a resource carried into this
   * scope by a copy/move (the runtime reads `provenance_copied_from` /
   * `provenance_promoted_from` from the `.lexmeta` and surfaces it per file).
   *
   * ✅ LIVE since the runtime surfacing landed — `case.workspace_tree` DOES
   * return this (`extensions/lexiun-core/src/gateway-methods/case-tree.ts`
   * reads the sidecar via `readProvenanceFromSidecar`). The field stays
   * optional: it is absent for files native to this scope, and the chip simply
   * does not render then.
   *   (Superseded 2026-07-27: this comment previously said the field was
   *   BLOCKED and never returned. That was true when written and is now wrong —
   *   the chip renders in production. Do not re-derive it as unavailable.)
   *   - `from`  human origin label ("Trend Casual", or "Workpad" for an
   *             org-promotion source).
   *   - `kind`  "copied" (case→case / case→org) | "promoted" (org→case).
   *
   * ⚠ `from` is NOT yet trustworthy as a human label: for case→case copies the
   * runtime still stamps the raw source `caseId` here
   * (`resource-copy-move.ts:474-477` — a read-time resolver is handed to the
   * `lexruntime-changes` lane). Consumers MUST route it through
   * `isDisplayableOriginLabel` (`components/shell/CaseFileNav.tsx`) and fall
   * back to generic wording — a raw entity id must never reach the lawyer.
   */
  provenance?: {
    from: string;
    kind: "copied" | "promoted";
  };
  /**
   * Workspace-file-aliases (P1, runtime contract C1) — the user-facing display
   * overlay. `alias` (when set) is the friendly label the SPA renders instead of
   * the raw machine `name`; `description` is the small note beneath it. Both
   * additive-optional (like `provenance` above) — absent → render `name`, no note.
   * Files resolve these from the `.lexmeta.json` `display.*` keys during the
   * server-side `case.workspace_tree` walk; renders NOTHING until the runtime P1
   * gate lands, then lights up with zero SPA change.
   */
  alias?: string;
  description?: string;
  /**
   * Workspace-file-tags (contract C4, frozen [tags-M1]) — the @tag HANDLE plane.
   * `tag` is the case-scope-unique reference handle (stored WITHOUT `@`), rendered
   * TAG-FIRST as the row's primary label (A8); `title` is the friendly citation
   * name (tree line 2 + public authored output). Both additive-optional — absent →
   * the row falls back to `alias`/`name`. Populated by the server walk once the
   * runtime reseed (R4) lands; renders with zero SPA change before then.
   */
  tag?: string;
  title?: string;
  // ── Ingest provenance (Acervo plan §B1, LIVE since lexiun-core `41663ffab`)
  //    — plucked from the SAME `.lexmeta` sidecar the walk already reads, so
  //    they cost zero extra I/O server-side. Mirrors the runtime's own
  //    `WorkspaceTreeFile` (keep the two in sync).
  //
  //    Three properties the consumer MUST respect — each verified at the
  //    source (`lx-kernel/src/storage/upload-attribution.ts::pluckIngestMeta`
  //    + `lexiun-core/src/gateway-methods/case-tree.ts`), not assumed:
  //
  //    1. INDEPENDENTLY optional. Each is emitted on its own truthiness test,
  //       so a file may carry `originalName` with no `modality`. Never treat
  //       them as one all-or-nothing group.
  //    2. Empty stamps are OMITTED, never sent as `""` — a legacy source with
  //       a blank `original_filename` arrives with the field absent, so there
  //       is no empty-string label to guard against.
  //    3. Absent ≠ stale. A file that was never ingested (an agent-authored
  //       artifact, a draft) legitimately carries none of them. "Obsolete"
  //       requires ALL THREE of: an INGEST zone (`sources/`, `raw-intake/`),
  //       `modality` missing, AND `ingestMeta` present on the envelope below.
  //       Rendering a missing `modality` as ⚠ outside that conjunction is a
  //       false warning on a healthy file. (Plan §6 F1 step 4.)
  //
  //    A CCM copy PRESERVES whatever the source had (`rewriteDestLexmeta`
  //    re-stamps only `case_id`/`org_id` and adds provenance) — but it cannot
  //    conjure keys that never existed, so a copy of a LEGACY pre-schema
  //    source arrives with none of them. **Never special-case
  //    `provenance` into "assume healthy":** a copied file is obsolete on
  //    exactly the same terms as any other, and an exemption would suppress a
  //    warning the file legitimately earned. The conjunction in (3) is the
  //    whole test.

  /** Server-routed ingest modality (`ingest_modality`) — the grouping key.
   *  OPEN vocabulary: an unrecognised value is a real modality this SPA has
   *  not labelled yet, NEVER an error state. */
  modality?: string;
  /** The filename the lawyer actually uploaded (`original_filename`) — the
   *  only string tying a frozen slug back to what they recognise. */
  originalName?: string;
  /** sha256 of the original bytes (`original_sha256`) — pairs a `raw-intake`
   *  entry with its `sources/` twin. */
  sourceId?: string;

  // ── Arrival provenance (D4 ruling 2026-07-31, lexiun-core `ee62146cc`) —
  //    complementary information, NEVER a grouping key: the operator ruled
  //    PURE MODALITY grouping, so these render on the expanded row + the
  //    content view only. Same independent-optionality rules as the three
  //    above.

  /**
   * The RAW arrival enum (`upload_source`): `lexide` | `whatsapp` | `email` |
   * `telegram` | `agent` | `api` | `migration`.
   *
   * The raw token crosses the wire and **the display label is deliberately
   * the SPA's** — map through `caseFiles.channels.<enum>`, never render this
   * value directly. Two consequences that matter:
   *   - `lexide` displays as **"app"**. A lawyer must never read the
   *     product's internal name (users-guide rendering contract rule 1).
   *   - the enum is OPEN, like `modality`: an unrecognised value renders
   *     VERBATIM rather than blank or "unknown". A closed map over a server
   *     vocabulary is the false-green defect class this codebase has shipped
   *     before.
   */
  channel?: string;
  /** ISO-8601 arrival timestamp — renders through `smartFileDate` beside the
   *  channel label. Absent → show the channel alone; both absent → render
   *  nothing (silence, never a placeholder). */
  uploadedAt?: string;

  /**
   * The ingest's own recorded OUTCOME (D5's FALHOU signal, lexiun-core
   * `f1f04e2ad`), read from the file's `.processed.md` companion:
   * `extraction.status === "ok"` → `"ok"`, every other known status
   * (`low_confidence`, `corrupt`, `extract_failed`, `unsupported`,
   * `egress_blocked`, …) → `"failed"`.
   *
   * **ABSENT MEANS UNKNOWN, NOT FINE.** An unreadable, missing, or
   * uninterpretable companion leaves the field off rather than defaulting to
   * `"ok"` — an unknown outcome painted green is a lie a lawyer cannot see
   * through. So branch on `=== "failed"` to warn and `=== "ok"` to reassure;
   * never treat `!== "failed"` as success.
   *
   * ⚠ **Served ONLY for `sources/*` entries** — the walk gates the companion
   * read on that prefix, because no other zone can have one. A `raw-intake/`
   * node therefore NEVER carries this, which is why the D5 badge renders on
   * the file's `sources/` row rather than inside the Pendente group (whose
   * members are by definition raw-intake files with no `sources/` twin, and
   * so no companion and no recorded outcome). See plan §6 F1 step 5.
   */
  ingestStatus?: "ok" | "failed";
}

export interface WorkspaceTreeFolder {
  /** Folder basename; the root folder uses the case id. */
  name: string;
  /** Case-root-relative path; the root folder is "". */
  path: string;
  isDirectory: true;
  folders: WorkspaceTreeFolder[];
  files: WorkspaceTreeFile[];
  /** Workspace-file-aliases (P1, C1) — folder display overlay (from
   *  `.lexiun/display-overrides.json`, D2). Same semantics as the file fields; a
   *  user alias WINS over the folder-map label. Additive-optional. */
  alias?: string;
  description?: string;
}

export interface CaseWorkspaceTreeResult {
  /** Absent in the `{scope:"org"}` response (the org workspace has no
   *  caseId — it's the per-user "Global" scope). Consumers must not
   *  rely on this when fetching org-scoped. */
  caseId?: string;
  /** `"org"` in the org-scope response; absent for case-scope. */
  scope?: "org";
  root: WorkspaceTreeFolder;
  /**
   * §B1 capability marker — stamped UNCONDITIONALLY by any post-B1 runtime
   * (lexiun-core `41663ffab`; `INGEST_META_CAPABILITY`), on the ONE handler
   * that serves BOTH case and org scope, and pinned by a test asserting the
   * SERIALIZED payload rather than the type.
   *
   * It exists because the SPA and the runtime image ship on INDEPENDENT
   * deploy axes, which makes two very different situations look identical
   * from a single case's tree:
   *
   *   - marker ABSENT  → this runtime predates B1 and serves the fields for
   *     NO file. Render the classic zone tree. Reading the universally-absent
   *     fields as "every file is stale" would paint the lawyer's whole
   *     workspace with ⚠ warnings about healthy files.
   *   - marker PRESENT → this runtime serves them, so a file lacking them
   *     really does lack the stamps → the obsolete row is honest.
   *
   * A capability gate on the ENVELOPE, not a per-file fallback: the SPA still
   * never derives a file's modality or label client-side (the no-fallback
   * ruling stays honored). Typed as the literal `1` so it is a marker, not a
   * flag anyone can meaningfully set to `false` — test presence, never truth.
   */
  ingestMeta?: 1;
}

// ── case.folder_map ──────────────────────────────────────────────────
// Presentation overlay joined onto `case.workspace_tree` BY FOLDER NAME
// (entry.key === on-disk folder.name) so the SPA can render friendly
// multilingual labels + emojis instead of raw slugs, hide internal
// folders, and display in a stable order. Runtime ships a seed map so
// it's always populated; the folder-organizer agent later layers
// per-case dynamic entries on top. See
// ai-docs/case-folder-map-contract.md §3 (entry) + §4 (envelope).

export interface CaseFolderMapParams {
  /** OPTIONAL (flat storage): resolves by `caseId` alone (same contract as tree). */
  clientId?: string;
  matterId?: string;
  caseId: string;
}

/** Org-scope folder-map request — see `OrgWorkspaceTreeParams`. */
export interface OrgFolderMapParams {
  scope: "org";
}

export interface FolderMapEntry {
  /** Joins to `WorkspaceTreeFolder.name`. */
  key: string;
  /** `fixed` → translate `caseFolders.<labelKey>`; `dynamic` and `virtual`
   *  → use the inline `label[locale]`.
   *
   *  ⚠ NEVER key label resolution off this field. `virtual` (the category
   *  folders seeded into `case.folder_map`) was added after the two resolvers
   *  were written, and because both gated their inline-label branch on
   *  `kind === "dynamic"` a virtual entry fell through to its RAW KEY in every
   *  locale — silently, since a raw key is a perfectly good-looking string.
   *  Resolve on "does it carry an inline `label` map", which is the property
   *  that actually decides the answer and stays true for the next kind too. */
  kind: "fixed" | "dynamic" | "virtual";
  /** Set when `kind === "fixed"`. */
  labelKey?: string;
  /** The inline label — keyed by BCP-47 locale tag
   *  (e.g. `{"pt-BR":"Petição","en-US":"Petition"}`). Carried by `dynamic`
   *  (agent-provided) and `virtual` (seeded category) entries alike. */
  label?: { [locale: string]: string };
  /**
   * Which tier the folder belongs to — the WRITE-POLICY axis, distinct from
   * `parent` (structure) and `kind` (label resolution).
   *
   * ⚠ **ABSENT MEANS UNKNOWN, NEVER UNCONSTRAINED.** A consumer deciding
   * whether a write is permitted must REFUSE an absent tier, not permit it —
   * treating a failed lookup as authorization is the defect the Generated tier
   * exists to prevent (`case-workspace-guide.md` §2.2-bis). Do NOT truthiness-
   * test this field: `if (entry.tier)` is false for an entry whose tier simply
   * has not been served yet, which is exactly the case that must refuse.
   *
   * ⚠ It is served TODAY (`case.ts:1706` spreads `buildFolderMap`'s entries
   * verbatim) but NOT yet on every entry, so an absent tier is currently
   * COMMON rather than anomalous. Any guard keyed on it must therefore wait
   * until SEED carries a tier on every entry — otherwise refuse-on-absent
   * refuses everything, including the Flexible tier that must permit writes.
   * Gated as a precondition on the A-ter §5 work (bus thread 20260806T215944Z).
   */
  tier?: "acervo" | "flexible" | "generated" | "system";
  /**
   * The folder this entry lives UNDER, when it is not a root.
   *
   * ⚠ **A ROOT is an entry with NO `parent`** — test `!entry.parent`, never a
   * name. Ruled as D-57 by `case-storage` (bus thread 20260806T182345Z) after
   * this lane found that SEED had stopped declaring `sources/email/` and
   * `sources/whatsapp/` at all, leaving them to render as raw lowercase
   * directory names and the locale keys D-46 preserved for them unreachable.
   *
   * `tier` deliberately does NOT serve this purpose: a subzone of `sources/`
   * is Acervo exactly as `sources/` is, so filtering on it would be wrong.
   *
   * Resolution stays NAME-scoped: `CaseFileNav` looks entries up by bare
   * folder name at any depth, so a subzone entry is keyed `"email"`, never
   * `"sources/email"`. `parent` says where it belongs; it is not a lookup path.
   */
  parent?: string;
  /** Display emoji prefix (e.g. "📥"). */
  emoji?: string;
  /** Ascending sort order; defaults to 500 if missing. */
  order?: number;
  /** When false, the SPA HIDES the folder + its subtree. Defaults
   *  to true. Used to suppress `.lexiun/` and other internal dirs. */
  visible?: boolean;
  /** Free-text purpose (debug/tooltip use only — not for headline). */
  purpose?: string;
}

export interface CaseFolderMapResult {
  /** Absent in the `{scope:"org"}` response. */
  caseId?: string;
  /** `"org"` in the org-scope response; absent for case-scope. */
  scope?: "org";
  version: number;
  caseType?: string;
  /** Provenance — `"seed"` | `"folders.json"` | a folder-organizer
   *  agent id. Surfaced in dev tooltips only. */
  generatedBy: string;
  /** Sorted by `order` then `key`. */
  folders: FolderMapEntry[];
  /**
   * ⚖ S7 — the content-category registry, the AUTHORITATIVE label source for
   * the `categorias` lens. Travels on the folder map but is NOT a folder: a
   * source is categorized, never moved.
   *
   * ⚠ Read labels from HERE, not from `folders[]` — this needs no `kind`
   * filter, so the virtual-folder label trap cannot arise. `categoryId` is the
   * join key; `labels` is what renders; `scope` is the MODEL's decision
   * material (long English prose) and is **NOT lawyer-facing**.
   */
  categories?: CaseCategoryEntry[];
  /**
   * Capability LEVEL, not a boolean. Absent ⇒ serve neither · `1` ⇒ category
   * fields, grouping only · `2` ⇒ the evaluation block AND the assess-content
   * harness are present.
   *
   * ⚠ **Gate with `>=`, never truthiness.** `if (categoryMeta)` passes at `1`
   * and would render an evaluation surface the runtime cannot fill — the exact
   * failure the marker exists to prevent, one level down. `>=` also lets a
   * future `3` degrade instead of breaking.
   */
  categoryMeta?: number;
}

/** One row of the content-category registry (`_sources_categories.jsonl`). */
export interface CaseCategoryEntry {
  categoryId: string;
  /** BCP-47 → label. Test-guaranteed for the three shipped locales. */
  labels: { [locale: string]: string };
  emoji?: string;
  order?: number;
}

export interface CaseContextParams {
  /** OPTIONAL (flat storage): the runtime reads context by `caseId` alone (openCaseById). */
  clientId?: string;
  matterId?: string;
  caseId: string;
}

/**
 * The `m-case-updated` mission's output — the case BRIEF. This is the content
 * the case-summary header exists to show (§3.4-ZERO, operator 2026-07-29:
 * *"the most important part … is exactly the case summary updated by
 * m-case-updated"*).
 *
 * **Every member is optional, and an EMPTY object is a CORRECT state** — a case
 * with no documents has nothing to summarise, so the harness writes an empty
 * brief rather than inventing one. Render "no summary yet"; do not read `{}` as
 * breakage. That is distinct from `brief` being ABSENT (no brief file at all).
 */
export interface CaseBrief {
  synopsis?: { text: string; lang?: string };
  /** OPEN, seeded, lowercase-kebab pt-BR vocabulary — the harness may emit
   *  outside the seed when the case genuinely is. **Render verbatim; never
   *  build a closed colour/label map over it** (that is B3's false-green defect
   *  waiting to happen — `CaseStatus` is closed and gets a gate, this does not). */
  phase?: { value: string; confidence?: string };
  /**
   * ⚠ These are OBJECTS, not strings — captured from a live payload, not
   * inferred. An earlier version of this type declared `string[]` and the
   * header rendered `[object Object]` to the operator on first look. The type
   * is a claim about a wire from a separately-deployed producer; verify it
   * against a real response before trusting it.
   */
  issues?: CaseBriefItem[];
  keyFacts?: CaseBriefItem[];
  claims?: CaseBriefItem[];
  risks?: CaseBriefItem[];
  value?: { value: number; currency: string; asOf?: string };

  /* ── The heading fields (contract §13.3-bis/-ter, harness `d9829b041`) ──
     Every member OPTIONAL; every list bounded to 12. **Absence means "could not
     be determined" — never render it as a zero, a `false`, or an empty state.** */

  /** The authored one-liner for the collapsed heading. ⚠ An OBJECT, not a
   *  string — typing it `string` ships `[object Object]`, which this file has
   *  already done once (see `CaseBriefItem`). */
  headline?: LabelMap;
  /** Curated SCALARS only. Structured facts get their own typed members below,
   *  because `value` is a string and flattening objects into it loses shape. */
  fields?: CaseBriefField[];
  /** ⚠ READ THESE, not `CaseContextResult.parties` — that top-level array stays
   *  EMPTY indefinitely (D-2 was reversed: the harness writes into the brief,
   *  because `case-brief.json` is on the §18.1.1 revision denylist and
   *  `parties.json` is not, so writing there would re-trigger the mission). */
  parties?: CaseBriefParty[];
  /** Same warning as `parties` — the brief's, not `case.context`'s. */
  deadlines?: CaseBriefDeadline[];
  /**
   * ⚠ **TRI-STATE BY OMISSION.** `true` under seal · `false` a real
   * determination that it is not · **absent = undetermined, NOT "no"**.
   *
   * A boolean rather than a `fields[]` entry on purpose: `fields[].value` is a
   * string, so `false` would serialise as `"nao"` — which is TRUTHY. A
   * confidentiality flag reading as SET when unset.
   */
  segredoDeJustica?: boolean;
}

/**
 * Display text in up to three locales. **`pt` is the only required member**, so
 * a consumer must never index a locale blindly. Use `pickLabel`.
 */
export interface LabelMap {
  pt: string;
  en?: string;
  es?: string;
}

export interface CaseBriefField {
  /** STABLE, machine-only. **NEVER DISPLAYED** — order and select on this,
   *  render `label`. Stable so ordering, tests and per-field treatment survive
   *  prompt drift. */
  key: string;
  /** The display text. ⚠ A `LabelMap`, NOT a string. */
  label: LabelMap;
  /** Plain display string, ≤240 chars. */
  value: string;
  sourceRefs?: string[];
  confidence?: BriefConfidence;
}

export interface CaseBriefParty {
  id: string;
  name: string;
  /** PROCEDURAL role (autor / réu / terceiro). **OPEN vocabulary — render
   *  verbatim**, no closed enum and no colour map. */
  role?: string;
  /**
   * ⚠ **TRI-STATE BY OMISSION — do NOT recover a boolean from absence.**
   *
   * The resolver REFUSES rather than guesses: it omits on no match, on a missing
   * `clientName`, and on more than one plausible match. Defaulting absent to
   * `false` would reintroduce exactly what the producer declines to invent — and
   * the failure is severe in both directions. A fabricated `false` on the
   * client's own party tells a lawyer they are acting AGAINST their own client;
   * a wrong `true` points them at the opposing party.
   *
   * **Three visual states.**
   */
  isOurClient?: boolean;
  sourceRefs?: string[];
  confidence?: BriefConfidence;
}

export interface CaseBriefDeadline {
  id: string;
  /**
   * ⚠ **AS-EXTRACTED, deliberately NOT normalised** — "a wrong date is worse
   * than an unparsed one". Formatting is the SPA's job; if it will not parse,
   * **show it verbatim** rather than guessing an interpretation.
   */
  date: string;
  description?: string;
  /** OPEN lowercase-kebab hint. **Render verbatim** — no closed enum. */
  kind?: string;
  sourceRefs?: string[];
  confidence?: BriefConfidence;
}

export type BriefConfidence = "high" | "medium" | "low";

/** One entry in a brief list. `text` is the only member the header renders —
 *  `sourceRefs` and `confidence` are provenance for surfaces that want to cite
 *  or filter, and a header that printed them would be a dashboard again. */
export interface CaseBriefItem {
  id?: string;
  text: string;
  sourceRefs?: string[];
  confidence?: string;
  /** `risks` only. */
  severity?: string;
}

export interface CaseBriefMeta {
  generatedAt?: string;
  /**
   * THREE states, and the third is ABSENCE.
   *
   * `false` ⇒ verified fresh · `true` ⇒ stale · **key absent ⇒ the digest could
   * not be recomputed, i.e. "could not verify"** — which is NOT fresh. A header
   * that renders nothing for both `false` and absent tells the lawyer the
   * summary is current on exactly the cases where nobody can say.
   */
  stale?: boolean;
  schemaVersion?: number;
}

export interface CaseContextResult {
  /** ⚠ Legacy field NAME: this is the case TITLE, not the brief. */
  summary: string;
  parties: import("./party").Party[];
  issues: string[];
  deadlines: import("./deadline").Deadline[];
  timeline: import("./timeline").TimelineEvent[];
  case?: {
    caseId?: string;
    title?: string;
    description?: string;
    status?: string;
    matterType?: string;
  };
  counts?: {
    documentCount?: number;
    pendingAiTasks?: number;
    processingStatus?: string;
  };
  brief?: CaseBrief;
  briefMeta?: CaseBriefMeta;
  /**
   * CLOSED vocabulary of FIELD NAMES whose value above is not the whole truth
   * (`briefDigest`, `documentCount`, `parties`, `deadlines`, `timeline`).
   *
   * ⚠ **A consumer MUST have a default branch.** Switch-casing these tokens
   * renders nothing for one it does not recognise, so the next field the
   * backend adds would vanish from the UI with no error anywhere — which is the
   * exact failure the closed-vocabulary ruling claims to prevent, just deferred.
   */
  incomplete?: string[];
}

/**
 * D4 — the shape every `*.list_strict` method returns.
 *
 * `items` is what the plain `*.list` sibling would have returned; `unreadable`
 * names the workspace files that could NOT be read (`"deadlines.json"`, …).
 *
 * **The two fields are independent, and that is the whole point.** A corrupt
 * file yields `items: []` AND a non-empty `unreadable` — so emptiness alone
 * never proves absence. Callers that merely RENDER a list can ignore
 * `unreadable`; callers that ASSERT something about the emptiness ("sem
 * prazos", "0 partes") must check it and stay silent when it is non-empty.
 *
 * A partial read is possible: some entries returned, others unreadable. Treat
 * a non-empty `unreadable` as "this collection is INCOMPLETE", not as "this
 * collection failed" — the items that did come back are real.
 */
export interface StrictCollectionResult<T> {
  items: T[];
  /** Workspace-relative filenames that could not be read. Empty ⇒ complete. */
  unreadable: string[];
}

/* ---------- Lifecycle status-change RPCs (C.6.deferred) ---------- */

export interface CaseSetStatusParams {
  clientId: string;
  matterId: string;
  caseId: string;
  status: string;
}

export interface CaseSetStatusResult {
  ok: true;
  status: string;
  prevStatus?: string;
  /** True when the RPC was a no-op because the case already had the target status. */
  unchanged?: boolean;
}

export interface MatterSetStatusParams {
  clientId: string;
  matterId: string;
  status: string;
}

export interface MatterSetStatusResult {
  ok: true;
  status: string;
  prevStatus?: string;
  unchanged?: boolean;
}

/* ---------- Contract-matter RPC (C.5) ---------- */

export type ClauseType =
  | "confidentiality"
  | "termination"
  | "renewal"
  | "penalty"
  | "governing_law"
  | "liability"
  | "other";
export type ClauseRisk = "low" | "medium" | "high";

export interface ContractClause {
  id: string;
  title: string;
  type?: ClauseType;
  risk?: ClauseRisk;
  summary?: string;
  sourceRef?: string;
}

export type ObligationOwner = "us" | "counterparty" | "both";
export type ObligationStatus =
  | "pending"
  | "in_progress"
  | "completed"
  | "overdue";

export interface ContractObligation {
  id: string;
  title: string;
  owner?: ObligationOwner;
  dueDate?: string;
  status?: ObligationStatus;
  description?: string;
}

export interface ContractContextParams {
  clientId: string;
  matterId: string;
  caseId: string;
}

export interface ContractContextResult {
  clauses: ContractClause[];
  obligations: ContractObligation[];
}

/* ---------- Advisory-matter RPC (C.5) ---------- */

export interface ResearchEntry {
  id: string;
  title: string;
  summary?: string;
  notes?: string;
  hypothesis?: string;
  createdAt?: string;
}

export type AuthorityType =
  | "statute"
  | "regulation"
  | "precedent"
  | "doctrine"
  | "other";
export type AuthorityRelevance = "strong" | "weak" | "contrary";

export interface Authority {
  id: string;
  citation: string;
  type?: AuthorityType;
  relevance?: AuthorityRelevance;
  summary?: string;
  url?: string;
}

export interface AdvisoryContextParams {
  clientId: string;
  matterId: string;
  caseId: string;
}

export interface AdvisoryContextResult {
  researchEntries: ResearchEntry[];
  authorities: Authority[];
}

/* ---------- Document RPC ---------- */

export interface DocumentListParams {
  /** OPTIONAL (flat storage): resolves by `caseId` alone via `resolveGatewayWorkspace`. */
  clientId?: string;
  matterId?: string;
  caseId: string;
  folder?: string;
  /** Phase 5.2c.3 — filter AIEditor-projected rows by documentType.
   *  Legacy process-scoped docs are returned unchanged regardless. */
  documentType?: DocumentType;
}

export interface DocumentReadParams {
  clientId: string;
  matterId: string;
  caseId: string;
  docId: string;
}

/** Org-scope document read — `document.read {scope:"org", docId}`
 *  (R3). Result shape identical (`DocumentReadResult`). */
export interface OrgDocumentReadParams {
  scope: "org";
  docId: string;
}

export interface DocumentReadResult {
  content: string;
  metadata: Record<string, unknown>;
}

/**
 * `document.read_bytes` — workspace-relative byte read backing the
 * `WorkspaceFilePane` viewers (lexruntime commit `a5c3bf7b`).
 *
 *   - `path` is workspace-relative ("raw-intake/contestacao.pdf",
 *     "sources/contestacao__contestacao__<ts>.pdf"). Runtime guards
 *     against `..`/absolute escapes via `WorkspaceFiles.resolveInside`.
 *   - Size cap: ~18 MB raw (WS-frame-bounded). Larger files return
 *     INVALID_PARAMS "exceeds preview limit"; SPA should render a
 *     "preview not available" affordance.
 *   - `mimeType` is inferred from extension (PDF/image/text); unknown
 *     extensions return `application/octet-stream`.
 */
export interface DocumentReadBytesParams {
  /** OPTIONAL (flat storage): resolves by `caseId` alone via `resolveGatewayWorkspace`. */
  clientId?: string;
  matterId?: string;
  caseId: string;
  /** Workspace-relative path (no leading slash). */
  path: string;
}

/**
 * Org-scope byte read — `document.read_bytes {scope:"org", path}`
 * against the per-user "Global" workspace (R3, lexruntime `9ed2d9e7`).
 * The runtime resolves the owner from `LEX_USER_ID`; the `path` is
 * workspace-relative just like the case variant. Result shape is
 * identical (`DocumentReadBytesResult`).
 */
export interface OrgDocumentReadBytesParams {
  scope: "org";
  /** Workspace-relative path (no leading slash). */
  path: string;
}

/* ---------- A-14 — document details (provenance + user notes) ------------
 * Contract: ai-docs/a14-kernel-half-runtime-miniplan.md §6, typed EXACTLY as
 * named there. ⚖ BOTH doors are PARENT-KEYED — the caller never names (and
 * never learns) the notes-sidecar suffix; the kernel computes both siblings.
 * That is the ruled (b) shape keeping the suffix out of the SPA entirely
 * (amended D-115; the SPA-wide grep pin is T9b / this repo's U7). */
export interface CaseDocumentDetailsParams {
  caseId: string;
  /** Workspace-relative path of the PARENT document (never a sidecar). */
  parentPath: string;
}
export interface DocumentProvenanceEntry {
  provider: string;
  path: string;
  query?: string;
  url?: string;
  ts: string;
}
export interface CaseDocumentDetailsResult {
  ok: true;
  /** ⚠ `null` ≠ `{entries: []}` — null means "no provenance RECORDED" (a real
   *  state: not a search doc); an empty entries array means "recorded, and
   *  empty". A FAILED read never arrives here at all — the seam throws
   *  instead of returning the shape that means "there is none". */
  provenance: { v: number; entries: DocumentProvenanceEntry[] } | null;
  /** The notes body, or null when no note exists yet. */
  notes: string | null;
  /** Provenance of the NOTE itself (measured, never inferred). */
  notesAuthoredBy: string | null;
  /** The PARENT document's own authorship stamp (concierge|mission|user). */
  authoredBy: string | null;
}
export interface CaseDocumentNoteWriteParams {
  caseId: string;
  /** The PARENT document's path — the kernel computes the sidecar. */
  parentPath: string;
  content: string;
}
export interface CaseDocumentNoteWriteResult {
  ok: boolean;
}

export interface DocumentReadBytesResult {
  /** Base64-encoded file bytes. Empty string for 0-byte files. */
  bytes: string;
  /** Inferred MIME type ("application/pdf", "image/png", ...) or
   *  "application/octet-stream" when the extension is unknown. */
  mimeType: string;
  /** Decoded byte length. Useful for size-display / sanity checks. */
  byteLength: number;
}

/**
 * `document.upload` — the runtime's verbatim-bytes-to-raw-intake/ + intake-pdf
 * trigger seam wired by `wip` commit `fb00d7fd` (closes the backend half of
 * `ai-docs/handoffs/lexide-upload-to-ingest-e2e-handoff.md`).
 *
 * Contract (mirrors `lexruntime/openclaw/extensions/lexiun-core/src/gateway-methods/document.ts`
 * `handleDocumentUpload`):
 *   - `name` — client-supplied filename. The runtime sanitizes it to the
 *     basename + rejects path segments / hidden-dot prefixes / >120 chars.
 *     For v1 the name MUST end in `.pdf`.
 *   - `bytes` — base64-encoded payload. Capped at ~18 MB raw (the WS gateway
 *     frame is 25 MB wire; base64 inflation is ~4/3).
 *   - `mimeType` — optional; when present must be `application/pdf` (v1 is
 *     born-digital PDF only; OCR + non-PDF media types are §7-deferred).
 */
export interface DocumentUploadParams {
  clientId: string;
  matterId: string;
  caseId: string;
  /** Filename including `.pdf` suffix; sanitized to basename server-side. */
  name: string;
  /** Base64-encoded file bytes. Capped at ~18 MB raw (WS frame headroom). */
  bytes: string;
  /** Optional MIME type. When supplied must be `application/pdf` for v1. */
  mimeType?: string;
}

/**
 * `document.upload_limits` — read-only platform policy (no params, scope-free).
 * The SPA pre-validates against these purely to fail FAST with a good message;
 * the server stays authoritative, since a client check is advisory and
 * bypassable.
 */
export interface DocumentUploadLimits {
  schemaVersion: number;
  /** Hard WS ceiling. Clamps EVERYTHING — see `effectiveMaxBytes`. */
  transportMaxBytes: number;
  maxFilesPerBatch: number;
  maxDepth: number;
  maxPathChars: number;
  /** Applied when an extension has no explicit cap. */
  defaultMaxBytes: number;
  /**
   * The authoritative ACCEPT list, derived server-side from the ROUTE TABLE
   * (`ALL_MEDIA_EXTS`), never from the cap table — a type with no explicit cap
   * is a normal accepted type (it uses `defaultMaxBytes`), not an unsupported
   * one. OPTIONAL because runtimes and this SPA deploy on independent axes;
   * when absent (older runtime) the picker falls back to the cap-table keys,
   * which is that runtime's historical behaviour.
   */
  acceptedExtensions?: string[];
  /** Per-extension caps, ALREADY IN BYTES. ⚠ The keys are NOT the accepted-
   *  extensions list — deriving the picker from them silently hid every type
   *  that rides on `defaultMaxBytes` (.txt/.md/.odt/.rtf…). Use
   *  `acceptedExtensions` above. */
  fileSizeBytes: Record<string, number>;
  deniedExtensions: string[];
  archive: {
    enabled: boolean;
    maxEntries: number;
    maxTotalUncompressedBytes: number;
    /** zip-inside-zip is refused. */
    nestedAllowed: boolean;
  };
  /**
   * ⚖ **T-5** — the HTTP escape hatch for containers too large for the WS frame.
   *
   * The WS path base64s the body (`useDocumentUpload`'s `fileToBase64`), so a
   * 28.5 MB zip becomes ~38 MB before it is even framed. This block advertises a
   * raw-binary POST route that sidesteps that entirely.
   *
   * ⚠ **OPTIONAL, and its ABSENCE IS MEANINGFUL — it means "old runtime".** The
   * SPA and the runtime deploy on independent axes, and this field landed in the
   * runtime AFTER the fleet's current image (measured 2026-08-10: the running
   * container served zero occurrences of it). When absent, the SPA MUST keep
   * today's behaviour and REFUSE an over-ceiling archive — never POST
   * optimistically, because the route does not exist there and a clear pt-BR
   * refusal would become a 404.
   *
   * Same rule as `acceptedExtensions` above and as `case.open`'s
   * `viewerPermissions`: gate on the capability's PRESENCE, never on optimism.
   * Plan: `ai-docs/lexide-t5-http-upload-transport-miniplan.md` §3.1.
   */
  httpUpload?: {
    /** Runtime path, e.g. `/api/lx/ingest/upload`. Never hardcode it. */
    path: string;
    /** Ceiling for THIS transport — the archive's real limit, and the number
     *  the refusal must quote (§3.2). Distinct from `transportMaxBytes`. */
    maxContainerBytes: number;
    /** When true the route accepts archives ONLY; a large non-archive stays
     *  refused at `transportMaxBytes`. */
    archiveOnly: boolean;
  };
}

/** One member extracted from an uploaded archive. */
export interface DocumentUploadExtracted {
  ref: string;
  name: string;
  modality: string;
  bytes: number;
}

/** One archive member the safeguards gate skipped. Present on an OTHERWISE
 *  SUCCESSFUL upload — per-file isolation means partial success is normal. */
export interface DocumentUploadRejected {
  name: string;
  code: string;
  /** Ready-to-show pt-BR sentence. Surface VERBATIM. */
  reasonPt: string;
}

export interface DocumentUploadResult {
  /** Workspace-relative ref where the bytes landed verbatim, e.g.
   *  `raw-intake/contestacao.pdf`. The audit-floor invariant: name kept
   *  exactly as the sanitized basename. */
  ref: string;
  /** Number of decoded bytes written to disk. */
  bytesWritten?: number;
  /** ARCHIVE DISCRIMINATOR. When true the upload was a `.zip` and the reply
   *  describes its expansion instead of a single document. The archive itself
   *  is deliberately NOT a source (no intake leaf) — its members are. */
  archive?: boolean;
  extracted?: DocumentUploadExtracted[];
  /** May be non-empty on a SUCCESSFUL upload. Never render as a failure. */
  rejected?: DocumentUploadRejected[];
  /** Mission ID for the intake-pdf run kicked atomically after the bytes
   *  land. Present only when the runtime had `runtimeOrgId + missionClient`
   *  wired AND the mission-open succeeded. Poll via the existing
   *  `/api/mission/missions/:id` route to drive the Lexiun Agents pane. */
  missionId?: string;
  /**
   * F3 / miniplan §6 (B6) — the run id of the SUBMISSION-REACTION turn the
   * runtime armed for this upload, when it armed one.
   *
   * Its presence is the SPA's cue to enter the ordinary prompt-submission
   * progress state, so the model's reaction streams into the thread exactly
   * like a reply to a typed prompt. Its VALUE is what makes that possible:
   * `activeRunId` must equal the incoming frames' `runId` or the deltas are
   * dropped as belonging to another run, so a bare boolean would light the
   * spinner and then fail to stream into it.
   *
   * ABSENT means "no reaction was armed" — today's pre-B6 build, an upload
   * whose mission never opened, and the failure branch. Absence therefore
   * arms NOTHING, which is deliberate: this is a POSITIVE marker, the same
   * discipline B2's discriminator adopted after absence-as-signal cost two
   * lanes a day. A spinner that appears on every upload and resolves only
   * sometimes is worse than no spinner.
   */
  reactionRunId?: string;
  /**
   * Set INSTEAD of `reactionRunId` when NO TURN COULD BE STARTED, so there is
   * nothing to wait for and no progress state is armed.
   *
   * Deliberately CAUSE-BLIND. Two known producers reach this one state, and
   * the SPA must not learn to tell them apart:
   *   - a synchronous ignition failure (a wedged gateway), and
   *   - the model-facing prompt being unavailable — missing, disabled or
   *     deleted in the lexserver Prompt Manager, or lexserver unreachable.
   * The second arrives BEFORE ignition is attempted at all, which is why the
   * older "ignition failed synchronously" wording here was narrower than the
   * contract (dynamic ingest prompts, `mission-ingest` 2026-08-03). Treat this
   * field as an open set: a future producer that also cannot start a turn
   * belongs in this same branch, and no consumer should switch on the string.
   *
   * The SPA renders nothing of its own here. The backend emits the
   * deterministic fallback line through the normal receipt plumbing, because
   * it is the only side that knows the turn's fate; one author per sentence.
   */
  reactionError?: string;
  /** Session key the mission was opened against (`type:"case"` shape).
   *  Round-trips through the §Q4 wake on terminal frames. */
  sessionKey?: string;
  /** Soft error: bytes ARE on disk (audit-floor invariant), but the
   *  mission-open failed downstream of the write. The SPA should render
   *  a "uploaded, retry processing" affordance + let the user re-trigger
   *  via `lx_delegate({pdf_ref: ref})`. */
  triggerError?: string;
}

/* ---------- AIEditor document RPCs (Phase 5.2b.2) ---------- */

export interface AiEditorMeta {
  docId: string;
  documentType: DocumentType;
  title: string;
  filename: string;
  status: DocumentStatus;
  matterType: MatterType;
  authorUserId?: string;
  createdAt: string;
  updatedAt: string;
  currentVersionHash: string | null;
  filedAt?: string;
  finalizedAt?: string;
}

export interface AiEditorVersionEntry {
  versionHash: string;
  previousHash: string | null;
  timestamp: string;
  toolCallId: string;
  authorRunId: string;
  authorUserId: string;
  promptSummary: string;
  patchStats: { added: number; removed: number };
}

export interface DocumentCreateParams {
  clientId: string;
  matterId: string;
  caseId: string;
  documentType: DocumentType;
  title: string;
  // Required (server-side also requires) — keeps the sidecar's
  // `matterType` authoritative for the Phase 5.3 matter-type gate.
  matterType: MatterType;
  authorUserId?: string;
}

export interface DocumentCreateResult {
  docId: string;
  documentType: DocumentType;
  filename: string;
  zonePath: string;
  docPath: string;
  meta: AiEditorMeta;
}

export interface DocumentSetStatusParams {
  clientId: string;
  matterId: string;
  caseId: string;
  docId: string;
  newStatus: DocumentStatus;
}

export interface DocumentSetStatusResult {
  meta: AiEditorMeta;
}

// ── document.set_display (workspace-file-aliases P2 · runtime contract C2) ──
// Sets/clears a file OR folder's user-facing alias + description overlay. The
// on-disk machine name NEVER changes — this is a presentation overlay (the
// rename story). Scope via the shared resolver: the case triple OR
// `{scope:"org"}` (D10). `alias`/`description`: a string SETS; `null` (or "")
// CLEARS (revert to raw name); omit to leave unchanged.

export interface DocumentSetDisplayParams {
  clientId?: string;
  matterId?: string;
  caseId?: string;
  /** Org ("Global") scope — omit the case triple. */
  scope?: "org";
  /** Case-root-relative path of the file/folder being aliased. */
  path: string;
  isDirectory: boolean;
  /** @deprecated workspace-file-tags — superseded by `title`; kept for back-compat. */
  alias?: string | null;
  /** Workspace-file-tags (C5): the `@tag` handle to set (no `@`). `tag: null` is
   *  REJECTED in Stage A (clearing rides reclamation, Stage B). */
  tag?: string | null;
  /** Workspace-file-tags (C5): the citation name (the former alias slot). */
  title?: string | null;
  description?: string | null;
}

/**
 * Discriminated on `ok`. Business outcomes (conflict / invalid / forbidden) ride
 * the SUCCESS frame so the SPA renders them inline; a `respond(false)` is reserved
 * for workspace-resolution / internal errors. Mirrors the frozen runtime union
 * (workspace-file-tags C5, frozen [tags-M1]).
 */
export type DocumentSetDisplayResult =
  | {
      ok: true;
      path: string;
      isDirectory: boolean;
      /** @deprecated workspace-file-tags — superseded by `title`. */
      alias: string | null;
      /** The `@tag` handle (no `@`) after the write — null when untagged. */
      tag: string | null;
      /** The citation name after the write — null when cleared. */
      title: string | null;
      description: string | null;
      updatedById: string;
      updatedAt: string;
    }
  | {
      ok: false;
      error: "display_name_conflict";
      /** The colliding sibling (D3). */
      conflict: { name: string; path: string };
    }
  | {
      ok: false;
      error: "invalid_display_name";
      reason: "empty" | "too_long" | "slash" | "control_char" | "leading_dot_or_underscore";
    }
  // ── workspace-file-tags (C5) ──
  | {
      ok: false;
      error: "invalid_tag";
      reason: "empty" | "too_short" | "too_long" | "leading_non_letter" | "bad_char";
    }
  | {
      ok: false;
      error: "tag_conflict";
      /** The file that already holds this tag (case-scope), named by its title. */
      holder: { path: string; title: string };
    }
  | { ok: false; error: "tag_reclaiming"; until_verified: true }
  | { ok: false; error: "forbidden_path" };

export interface DocumentListVersionsParams {
  clientId: string;
  matterId: string;
  caseId: string;
  docId: string;
  offset?: number;
  limit?: number;
}

export interface DocumentListVersionsResult {
  versions: AiEditorVersionEntry[];
}

export interface DocumentReadVersionParams {
  clientId: string;
  matterId: string;
  caseId: string;
  docId: string;
  versionHash: string;
}

export interface DocumentReadVersionResult {
  content: string;
  versionHash: string;
}

export interface DocumentReadPromptParams extends DocumentReadVersionParams {}

export interface DocumentReadPromptResult {
  prompt: string | null;
  versionHash: string;
}

/* ---------- Data query RPC ---------- */

export interface PartyListParams {
  /** OPTIONAL (flat storage): case-scoped reads resolve by `caseId` alone (runOnCase
   *  → resolveGatewayWorkspace). Send both parents when known. */
  clientId?: string;
  matterId?: string;
  caseId: string;
}

export interface DeadlineListParams {
  clientId?: string;
  matterId?: string;
  caseId: string;
}

export interface TimelineListParams {
  clientId?: string;
  matterId?: string;
  caseId: string;
  processoId?: string;
}

export interface PetitionReadParams {
  clientId: string;
  matterId: string;
  caseId: string;
  petitionId: string;
}

export interface PetitionReadResult {
  content: string;
  metadata: Record<string, unknown>;
  status: string;
}

export interface PetitionUpdateParams {
  clientId: string;
  matterId: string;
  caseId: string;
  petitionId: string;
  content: string;
  versionHash: string;
}

export interface PetitionUpdateResult {
  ok: boolean;
  newHash: string;
}

/* ---------- Search RPC ---------- */

export interface SearchCaseParams {
  clientId: string;
  matterId: string;
  caseId: string;
  query: string;
}

/** Org-scope FTS — `search.case {scope:"org", query}` against the
 *  per-user "Global" workspace (R3). Result shape identical
 *  (`SearchResult[]`). */
export interface OrgSearchParams {
  scope: "org";
  query: string;
}

// D5a: `SearchGlobalParams` removed — the runtime's legacy `search.global`
// method was retired in Phase C. Use `search.case` / `search.matter` /
// `search.client` instead.

/** Phase F — `search.matter` federated query across a matter's cases. */
export interface SearchMatterParams {
  clientId: string;
  matterId: string;
  /** Required for the cascade-access gate. */
  userId: string;
  /** Required for the cascade-access gate. */
  orgId: string;
  query: string;
  limit?: number;
}

/** Phase F — `search.client` federated query across a client's matters + cases. */
export interface SearchClientParams {
  clientId: string;
  /** Required for the cascade-access gate. */
  userId: string;
  /** Required for the cascade-access gate. */
  orgId: string;
  query: string;
  limit?: number;
}

export interface SearchResult {
  type: "case" | "document" | "directive" | "party";
  id: string;
  title: string;
  snippet: string;
  relevance: number;
  caseId?: string;
}

/* ---------- Task RPC ---------- */

export interface TaskSpawnParams {
  type: string;
  params: Record<string, unknown>;
}

export interface TaskSpawnResult {
  taskId: string;
  status: string;
}

export interface TaskCancelParams {
  taskId: string;
}

export interface TaskStatusParams {
  taskId: string;
}

export interface TaskStatus {
  taskId: string;
  type: string;
  state: "pending" | "running" | "completed" | "failed" | "cancelled";
  progress: number;
  step?: string;
  output?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

/* ---------- Context RPC ---------- */

export interface ContextResolveParams {
  navState: Record<string, unknown>;
}

export interface ContextResolveResult {
  sessionKey: string;
  contextType: string;
  title: string;
}

/* ---------- Research RPC ---------- */

/** Phase 6.4.2 — single citation attached to a research topic.
 *  Mirrors lexruntime/openclaw/extensions/lexiun/src/services/
 *  research-topic-store.ts:Citation. */
export interface ResearchCitation {
  /** Stable client-generated id (ULID-ish). */
  id: string;
  title: string;
  url?: string;
  source?: string;
  excerpt?: string;
  /** ISO 8601. */
  addedAt: string;
}

export interface ResearchTopic {
  topicId: string;
  title: string;
  sessionKey: string;
  directive?: string;
  /** Phase 6.4.2 — citations attached to this topic. */
  citations?: ResearchCitation[];
  createdAt: string;
  updatedAt: string;
}

export interface ResearchCreateParams {
  title: string;
}

export interface ResearchCreateResult {
  topicId: string;
  sessionKey: string;
}

/* ---------- Source assessment (`.info` sidecar — Part G) ----------
 *
 * Schema mirror of the `.info` frontmatter defined in
 * `ai-docs/mission-ingest-implementation-plan.md` §13.1(a).
 *
 * `.info.md` is HIDDEN from the LexIDE file tree (it lives next to a frozen
 * `sources/<stem>.<ext>` original) but its CONTENT is user-exposed — the
 * lawyer reads and edits it conversationally via the concierge. This
 * interface is what `case.source_assessment` (PENDING runtime endpoint —
 * see handoff `source-info-viewer-lexide-handoff-2026-06-27.md` §5/§7) will
 * return per source.
 *
 * `role` is OPEN free-text (consultivo / M&A / regulatory matters coin
 * their own roles). The seeds in `SUGGESTED_ROLE_SEEDS` (in
 * `useSourceAssessment.ts`) are autocomplete hints, NEVER a closed enum.
 */
export type SourceAssessmentStatus = "pending" | "assessed" | "declined";
export type SourceAssessmentWeight = "high" | "medium" | "low";

export interface SourceAssessment {
  /** sha256 of the original bytes — the stable binding key (survives re-ingest). */
  sourceId: string;
  /** The frozen-original's basename (e.g. `audio-x__audio__20260626-174039.ogg`). */
  processedName: string;
  /** ISO when the stub was written. */
  createdAt: string;
  status: SourceAssessmentStatus;
  /** Doctype→role map output from `intake-*` classify; the lawyer confirms/overrides. */
  suggestedRole?: string;
  /** Open free-text — the artifact's legal purpose. Pending until the lawyer fills it. */
  role?: string;
  weight?: SourceAssessmentWeight;
  relevance?: string;
  actors?: string;
  notes?: string;
  /** Filled at assess time (the §13.1(d) writer stamps these). */
  assessedBy?: string;
  /** ISO. */
  assessedAt?: string;

  // ── The MACHINE evaluation — a different author from everything above ─────
  //
  // ⚠ These are NOT the lawyer's assessment and must never be presented as it.
  // Everything above is the lawyer's (pending until they fill it); everything
  // here is written deterministically at ingest by a model. A source can, and
  // routinely does, carry a complete machine evaluation while the lawyer's
  // assessment is still `pending` — that combination is the NORMAL state of a
  // freshly ingested document, not a half-written record.
  //
  // 🔴 Added 2026-08-09 after an operator report: these fields were on disk in
  // every `.info.md` and the parser DROPPED them, so the viewer showed an empty
  // pane plus "Avaliar agora" for a document ingest had already analysed. The
  // analysis existed the whole time and no surface could reach it.
  /** Model-written précis of the document. The "analysis" a reader expects. */
  summary?: string;
  /** Why the model classified it as it did. */
  evaluationRationale?: string;
  /** POSITIVE assertion — `ok` | `failed` | `skipped_no_content`. Never inferred
   *  from absence: absent means the runtime did not evaluate, not that it failed. */
  evaluationStatus?: string;
  /** Which model produced the evaluation — provenance the lawyer can weigh. */
  evaluatedByModel?: string;
  /** ISO. */
  evaluatedAt?: string;
  /** Deterministic doctype→category at ingest; the lawyer may override. */
  suggestedCategory?: string;
  category?: string;
}

export interface CaseSourceAssessmentParams {
  clientId: string;
  matterId: string;
  caseId: string;
  /** sha256 source_id from the catalog (or the badge that drove this open). */
  sourceId: string;
  /**
   * ⚠ **The TWIN discriminator — pass it whenever you have the catalog row.**
   *
   * `sourceId` is the sha256 of the ORIGINAL BYTES, so the same document
   * ingested twice yields ONE id naming TWO files, and a sourceId-only resolve
   * picks an arbitrary twin. Send `row.companion_ref` verbatim from the
   * `case.source_catalog` row (`sources/<stem>.processed.md`) and the runtime
   * derives the right `.info`: malformed ⇒ INVALID_PARAMS, mismatched
   * `source_id` ⇒ NOT_FOUND, so no SPA-side validation is needed.
   *
   * OPTIONAL only for wire compatibility with pre-`a5a8a717c` runtimes —
   * omitting it is NOT a no-op, it silently restores the arbitrary-twin pick.
   */
  companionRef?: string;
}

export interface CaseSourceAssessmentResult {
  assessment: SourceAssessment;
}

/**
 * `case.source_assess_submit` — DEP #4 channel "option B" (lexruntime
 * commit `50a2cf06`): the standalone assessment panel POSTs the
 * lawyer's values here directly (no chat session required). Funnels to
 * the SAME kernel writer (`assessSourceInfo`, LWW) as the §Q4 / awaitUser
 * chat cards that route through the concierge `lx_source_assess` tool
 * (option A) — no second-writer drift. Accepts re-assessment of an
 * already-`assessed` source (overwrites the editable set). `decline:true`
 * records `status:declined`; otherwise `role` + `weight` are required.
 */
export interface CaseSourceAssessSubmitParams {
  clientId: string;
  matterId: string;
  caseId: string;
  sourceId: string;
  /** When true, records `status:declined` and ignores the value fields. */
  decline?: boolean;
  /** Required unless `decline:true`. Open free-text per §13.1(a). */
  role?: string;
  /** Required unless `decline:true`. */
  weight?: SourceAssessmentWeight;
  relevance?: string;
  actors?: string;
  notes?: string;
  /**
   * ⚠ **The TWIN discriminator — pass it whenever you have the catalog row.**
   *
   * `sourceId` is the sha256 of the ORIGINAL BYTES, so the same document
   * ingested twice yields ONE id naming TWO files, and a sourceId-only resolve
   * picks an arbitrary twin. Send `row.companion_ref` verbatim from the
   * `case.source_catalog` row (`sources/<stem>.processed.md`) and the runtime
   * derives the right `.info`: malformed ⇒ INVALID_PARAMS, mismatched
   * `source_id` ⇒ NOT_FOUND, so no SPA-side validation is needed.
   *
   * OPTIONAL only for wire compatibility with pre-`a5a8a717c` runtimes —
   * omitting it is NOT a no-op, it silently restores the arbitrary-twin pick.
   */
  companionRef?: string;
}

export interface CaseSourceAssessSubmitResult {
  /** The updated assessment after the kernel writer's overwrite —
   *  matches `case.source_assessment` so the caller can hydrate the
   *  panel immediately without a follow-up GET. */
  assessment: SourceAssessment;
}

/**
 * `case.source_catalog` — reach path for the sole `_index.json` writer
 * (`index-builder`). Backs the file-tree source-row BADGE. The runtime
 * surfaces each row's `.info` census via a NESTED `assessment` object
 * (NOT the flat `info_*` keys the dep handoff originally imagined —
 * lexruntime handback locked this 2026-06-28); the badge consumes
 * `row.assessment.{status, role, weight}` per source.
 *
 * Returns `{ catalog: { sources: [] } }` for a case with no sources
 * yet — not an error.
 *
 * Staleness caveat (lexruntime handback §6): the upload m-ingest mission
 * is single-node today (intake-leaf only, no index-builder), so the
 * catalog doesn't rebuild on upload. New sources have their `.info`
 * stub on disk + the dedicated `case.source_assessment` GET picks
 * them up; badges go stale until the m-ingest-verify reconciler runs.
 */
export interface CaseSourceCatalogParams {
  clientId: string;
  matterId: string;
  caseId: string;
}

export interface CaseSourceCatalogRow {
  source_id: string;
  processed_name: string;
  processed_ref?: string;
  companion_ref?: string;
  doctype?: string;
  h1?: string;
  parties?: string[];
  doc_date?: string;
  /** Extraction status — `"ok"` / `"low_confidence"` / etc. Distinct
   *  from the nested `assessment.status` below. */
  status?: string;
  /**
   * ⚖ S7 — the content category. ⚠ **Corrected 2026-08-07:** an earlier version
   * of this note said the category is "written DETERMINISTICALLY AT INGEST from
   * a doctype→category table (no LLM, no turn)". That design was STRUCK (D-4)
   * and describing it here would have had the next reader rebuild it. The live
   * split is: `suggested_category` is the doctype PRIOR (written at ingest, so
   * present from birth) and `category` is the VERDICT from the `assess-content`
   * harness. ⚠ Both live NESTED under `assessment`, not at top level.
   *
   * ⚠ **Group on the EFFECTIVE category — `category ?? suggested_category`** —
   * never on "was it assessed". Keying off assessment would file every fresh
   * source under "awaiting classification" while the server had already
   * classified it: a false claim built from a negative.
   *
   * ⚠ **Values are SERVER-OWNED DATA, not constants.** Join whatever the
   * payload serves against `folder_map.categories[]`; never hardcode a list.
   * They have already changed language once (pt → en), and a hardcoded list of
   * either language renders an EMPTY axis, which reads to a lawyer as
   * "nothing is classified".
   */
  category?: string;
  /** ⚠ TOP-LEVEL MIRRORS, tolerated but NOT the live shape — the wire nests
   *  both under `assessment` (see below). Kept declared so a flattened row
   *  from a future writer still groups instead of vanishing. */
  suggested_category?: string;
  /**
   * ⚖ COMPOUND-UPLOAD GROUPING (`compound-upload-grouping-miniplan.md`, S3).
   *
   * `node_id` — present when this row IS the head of a compound upload (a
   * WhatsApp conversation's transcript today; nested zips and email threads by
   * the same contract later). Opaque; minted by `archive-ingest` at plan time.
   * Never parse it.
   *
   * `parent_id` — present when this row BELONGS to a compound; references
   * another row's `node_id`. Chaining is allowed, so the shape is a tree, not
   * one level.
   *
   * ⚠ **A DANGLING `parent_id` MUST RENDER FLAT** — never hidden, never an
   * error. That is not defensive coding, it is the contract's forward-compat
   * hook: plain-zip members are stamped from day one and resolve to nothing
   * until their upload-node rendering ships, with zero re-ingest. It is also
   * the same rule this SPA already applies to a file with no catalog row at
   * all — grouping changes WHERE a row renders, never WHETHER.
   *
   * ⚠ Neither field is a stem or a `source_id`. `source_id` is a sha256 of the
   * original bytes, so one id can name N files (twins) — a parent pointer on it
   * is permanently ambiguous. These ride the FILE.
   */
  node_id?: string;
  parent_id?: string;
  /** The archive entry's full internal path as the lawyer had it, when this row
   *  came out of an archive. Promoted into the catalog by S3 so zip-internal
   *  folder structure stays derivable (`dirname`) without re-ingest. */
  original_name?: string;
  /**
   * The `.info` census the badge reads. `null` when no `.info` exists for the
   * source (legacy pre-F1).
   *
   * ⚠ **This is where the CATEGORY axis actually lives.** The index-builder is
   * the sole `_index.json` writer and emits both category fields NESTED here,
   * not at top level — the runtime's gateway docblock states it, and reading
   * them at top level made every row resolve to `undefined`, so the whole
   * Acervo landed in "sem categoria" (fixed 2026-08-07, diagnosed by
   * `mission-ingest` off a live case). Group via `effectiveCategory`, never by
   * reaching into either shape directly.
   */
  assessment: {
    status: SourceAssessmentStatus;
    suggested_role?: string;
    role?: string;
    weight?: SourceAssessmentWeight;
    /** The VERDICT, written by the `assess-content` harness. */
    category?: string;
    /** The doctype PRIOR, written at ingest — present from birth. */
    suggested_category?: string;
    /** Positive assertion, never inferred from absence. Requires `categoryMeta >= 2`. */
    evaluation_status?: string;
  } | null;
}

export interface CaseSourceCatalogResult {
  caseId?: string;
  catalog: {
    version?: number;
    count?: number;
    sources: CaseSourceCatalogRow[];
  };
  /**
   * S7 — the SERVING-RUNTIME capability LEVEL for content categories, emitted
   * on THIS envelope (`respond(true, { caseId, catalog, categoryMeta })`).
   * Verified against the rolled runtime bundle, not assumed: it rides the
   * catalog reply, NOT `case.open`'s `capabilities` and NOT the folder map.
   *
   * ⚠ **Gate with `>=`, never truthiness** — see the docblock on the other
   * `categoryMeta` in this file. `if (categoryMeta)` passes at `1` and would
   * render an evaluation surface the runtime cannot fill; a future `3` must
   * degrade rather than break.
   *
   * Absent ⇒ the runtime serves neither, so offer NO axis. That is the correct
   * degradation: a category lens the runtime cannot back would show every
   * source as uncategorized and read as data loss.
   */
  categoryMeta?: number;
}

/* ---------- runtime.system.enqueue_event (hidden SPA→concierge directive) ----------
 *
 * Claude-Code-style hidden-prompt channel — shipped 2026-06-28 by the
 * lexruntime agent (`05db5028` lexiun-core +
 * `source-info-viewer-hidden-event-lexruntime-dep-handback-2026-06-28.md`).
 * The directive lands as an OpenClaw system event (role
 * `custom_message` in the agent transcript), NOT as a user-visible chat
 * turn. The concierge sees it in its next turn and acts on it; only the
 * agent's RESPONSE renders to the user.
 *
 * Signature deviation from the original handoff: the handler builds the
 * case session key via `buildLxSessionKey({type:"case", …})`, so the
 * SPA must pass `clientId / matterId / caseId` — the active-context
 * store carries these.
 *
 * `text` is **SPA-composed** (`trusted: true` server-side); never feed
 * raw user-typed strings through this seam — prompt-injection hygiene.
 *
 * Heartbeat caveat (handback §4): the runtime has heartbeat DISABLED
 * in dev, so the directive is observed on the concierge's NEXT turn,
 * not proactively. UX needs to surface a "sent" confirmation since the
 * agent's response won't materialize until the user types something.
 */
export interface RuntimeSystemEnqueueEventParams {
  clientId: string;
  matterId: string;
  caseId: string;
  /** Non-empty, ≤ 8000 chars. */
  text: string;
  /** Provenance hint — surfaces in observability + becomes the OpenClaw
   *  event `contextKey` + heartbeat reason. Defaults server-side to
   *  `"lexide-spa"` when omitted. Examples:
   *    "lexide-spa:source-assess-pane"
   *    "lexide-spa:draft-suggestion-button"   */
  source?: string;
}

export interface RuntimeSystemEnqueueEventResult {
  ok: true;
}

/* ---------- document.copy / document.move (cross-scope resource ops — CCM R4) ----------
 *
 * Cross-scope copy/move of a `sources/` RESOURCE between scopes. The
 * runtime backend (lexruntime `ce359f0f`) carries the WHOLE companion set
 * (frozen original + `.processed.md` + `.info.md` + `.lexmeta.json`),
 * re-stamps the destination `.lexmeta` (case_id/org_id + provenance, while
 * preserving `uploaded_by_*` audit truth), and reindexes the destination
 * `sources/_index`. A `move` is copy-then-delete-source (verified first, so
 * a move can never lose the resource). The SPA sends ONE RPC and reflects
 * the result — it does not manage sidecars.
 *
 * Envelope (`document.copy` and `document.move` share the SAME shape):
 *   - `from` REQUIRES a `path` (the source resource, e.g. "sources/x.pdf").
 *   - `to` is a SCOPE ADDRESS ONLY (no path) — the destination keeps the
 *     source's canonical `sources/<name>` ref; there is no rename/folder
 *     choice in v1.
 *   - A `case` address requires the full `{clientId, matterId, caseId}`
 *     triple. An `org` address is just `{scope:"org"}` — the owner is the
 *     runtime's `LEX_USER_ID`, never a param (same rule as the R3 reads).
 *
 * Allowed scope matrix (enforced server-side): `case→case`, `org→case`
 * (a PROMOTION — `kind:"promoted"`), `case→org` are allowed; `org→org` and
 * any same-workspace pairing are rejected `INVALID_PARAMS`. So the picker
 * MUST exclude the current scope and must not offer "Global → Global".
 *
 * Contract: ai-docs/handoffs/ccm-lexide-copy-move-ui-handoff-2026-06-30.md
 * §2 + lexruntime resource-copy-move.ts.
 */
export type ScopeAddress =
  | { scope: "case"; clientId: string; matterId: string; caseId: string; path?: string }
  | { scope: "org"; path?: string };

export interface DocumentCopyMoveParams {
  /** SOURCE — REQUIRES a path (the resource being copied/moved). */
  from: ScopeAddress & { path: string };
  /** DESTINATION — scope address only (no path; the dest keeps the
   *  source's relative ref). */
  to: ScopeAddress;
}

export interface DocumentCopyMoveResult {
  mode: "copy" | "move";
  /** Destination refs written: the blob + the companions that existed.
   *  The `.lexmeta` sidecar travels WITH the blob and is NOT listed here. */
  copied: string[];
  provenance: {
    /** Source caseId, or the literal "org" for an org source. */
    from: string;
    path: string;
    by: string;
    at: string;
    /** "promoted" = org→case; "copied" otherwise. */
    kind: "promoted" | "copied";
  };
  /** `to`/`from` `sources/_index` re-derivation flags. `from` is true only
   *  after a `move` of a `sources/` resource. */
  reindexed: { to: boolean; from: boolean };
}

/**
 * `document.delete` — the Acervo delete (the D-2/D-3 USER-plane escape; the
 * AGENT plane stays refused inside `sources/`). Contract:
 * `ai-docs/handoffs/acervo-file-delete-feasibility-2026-08-01.md` §7.
 *
 * CASE SCOPE ONLY — there is no `scope:"org"` variant, deliberately: the org
 * workspace has no case owner, so the "owner only" ruling has no meaning there
 * and the runtime refuses it rather than inventing one.
 */
export interface DocumentDeleteParams {
  caseId: string;
  /** Cascade context for the role resolution. Absent on a flat/unassigned case. */
  clientId?: string;
  matterId?: string;
  /**
   * The FROZEN ORIGINAL's workspace-relative ref (`sources/<stem>.<ext>`) — the
   * same value the Acervo row already carries. Its whole member set (the
   * `.processed.md` / `.info.md` / `.lexmeta.json` / `.messages.json`
   * companions, plus the paired `raw-intake/` twin) goes with it: a partial
   * delete is worse than none, because the census either regenerates the
   * companion (the delete undoes itself) or reports a permanent unrepairable
   * phantom.
   */
  path: string;
  /**
   * REQUIRED `true`. The operator's gate is the SPA confirm dialog; this is the
   * interlock that keeps a mis-wired or replayed call from deleting without
   * one, mirroring `lx_delete`'s explicit-confirm discipline.
   */
  confirmed: true;
}

/**
 * Pinned against the SHIPPED handler (`383ce915e`), not an earlier draft — the
 * work-in-progress version returned a single `handle?: string`, and building
 * against it would have typed away the multi-handle case entirely.
 */
export interface DocumentDeleteResult {
  /**
   * Every workspace-relative ref actually unlinked — the whole member set,
   * spanning BOTH `sources/` and `raw-intake/`.
   *
   * ⚠ **NOT A LAWYER-FACING QUANTITY. Do not render `deleted.length`.** Since
   * `a2baf5705` the set includes one `.lexmeta.json` attribution sidecar per
   * MEMBER, and `isInternalFile` deliberately hides every one of those from the
   * tree — so a typical PDF source reports **6** entries (frozen +
   * `.processed.md` + `.info.md` + three sidecars) for what the lawyer sees as
   * ONE file. "6 arquivos removidos" is a number they cannot reconcile with
   * anything on screen, and it reads as a phantom-file bug.
   *
   * The delete confirmation needs no arithmetic at all — "removido do caso" is
   * the whole message. If a count is ever genuinely wanted, filter first:
   * `deleted.filter((r) => !r.endsWith(".lexmeta.json"))`. Useful as-is for
   * logging and diagnostics.
   */
  deleted: string[];
  /**
   * The `@handles` RETIRED (no `@`). **PLURAL, and `[]` is normal** — a source
   * is not required to carry a handle, and one source can be the target of
   * SEVERAL registry keys (including one on its raw-intake name), which a
   * single-valued field would have hidden.
   *
   * Retired means marked `reclaiming`, never freed: a freed handle can be
   * reissued, and every earlier `@ref` in chat or a draft would then silently
   * resolve to a DIFFERENT document — worse than a broken link, because a
   * broken link is visible and a re-pointed one is not.
   */
  handlesRetired: string[];
  /**
   * Count of FILES removed from `raw-intake/` (a twin is normally two: the blob
   * plus its `.lexmeta` sidecar), not a count of originals.
   *
   * ⚠ `0` IS AMBIGUOUS BY CONSTRUCTION — it means either "there was no twin" or
   * "the twin was deliberately KEPT because a surviving source shares those
   * bytes". Do not render it as a fact ("0 originals removed"); it is an
   * operator/diagnostic number, and the two states it collapses are different
   * sentences.
   */
  rawIntakeRemoved: number;
  /**
   * Whether `sources/_index.json` + `_INDEX.md` were re-derived. **`false` does
   * NOT mean the delete failed** — the files are gone either way; it means the
   * catalog the AGENT reads may still list the row until the next rebuild.
   * Worth telling the user, precisely because the tree (which reads disk) will
   * look correct while the assistant's view does not.
   */
  reindexed: boolean;
}

/* ---------- WebSocket connection state ---------- */

export type GatewayConnectionState = "disconnected" | "connecting" | "connected" | "reconnecting" | "error";
